import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  IconButton,
  Link,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import NavigationIcon from "@mui/icons-material/Navigation";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import apiService, {
  parseSearchPaymentDetailsResponse,
} from "../../../services/api";
import useHubs from "../../../hooks/useHubs";
import useRBAC, { ROLES } from "../../../hooks/useRBAC";
import { useAppSelector } from "../../../hooks/useAuth";
import type { RootState } from "../../../store";
import { getRoleHubRelations } from "../../../utils/cookieUtils";
import useCodeCommonByCategory from "../../../hooks/useCodeCommonByCategory";
import CustomLoading from "../../../components/common/CustomLoading";
import { useNotification } from "../../../contexts/NotificationContext";
import {
  SelectVehicleModelStep,
  SelectVehicleStep,
  SelectPlanStep,
  type VehicleModelOption,
  type YourSelectionVehicleDetailStrings,
  type VehicleOption,
  type PlanOption,
  type CostBreakdownItem,
} from "../create-booking-steps";
import { isIndianWallClockAfterTwoPm } from "../../../utils/formatters";
import {
  THEME_PRIMARY,
  THEME_PRIMARY_LIGHT,
  THEME_PRIMARY_TINT,
} from "../bookingTheme";
import dayjs from "dayjs";
import {
  pickVehicleCardImageFromApiField,
  readVehicleImagesJsonFromRow,
} from "../../../utils/mediaUtils";
import {
  CREATE_BOOKING_SK,
  clearCreateBookingDraft,
} from "../createBookingSessionStorage";

const BASE_IMAGE_URL = `${process.env.REACT_APP_BASE_IMAGE_URL || ""}`.replace(
  /\/?$/,
  "/",
);
const steps = [
  "Select Hub",
  "Select Vehicle Model",
  "Select Vehicle",
  "Select Plan",
];

const KYC_INCOMPLETE_VEHICLE_MESSAGE =
  "Your KYC is not completed for this vehicle.";
const KYC_HIGH_SPEED_DL_MESSAGE =
  "High-speed vehicles require verified KYC and driving licence. Complete licence verification to continue.";
const KYC_NOT_FOUND_DEFAULT_MESSAGE = "KYC not found for this user";

type CustomerKycState = {
  loading: boolean;
  checked: boolean;
  verified: boolean;
  hasDlOcrFullname: boolean;
  /** False when API reports no KYC or returns an empty list — blocks advancing from hub step. */
  hasKycRecord: boolean;
  missingMessage: string | null;
};

function kycDlOcrFullnamePresent(primary: unknown): boolean {
  if (primary == null || typeof primary !== "object") return false;
  const p = primary as { kycdetails?: unknown; kycDetails?: unknown };
  const d = p.kycdetails ?? p.kycDetails;
  if (d == null || typeof d !== "object") return false;
  const rec = d as { dl_ocr_fullname?: unknown; dl_ocr_fullName?: unknown };
  const raw = rec.dl_ocr_fullname ?? rec.dl_ocr_fullName;
  return String(raw ?? "").trim() !== "";
}

function pickKycListFromResponse(res: unknown): any[] {
  const r = res as
    | { data?: unknown; result?: unknown }
    | unknown[]
    | null
    | undefined;
  if (r == null) return [];
  if (Array.isArray(r)) return r;
  const obj = r as { data?: unknown; result?: unknown };
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.result)) return obj.result;
  return [];
}

const COUPON_APPLY_NOTIFY_MS = 3000;

function readCouponValidityMessage(body: unknown): string | null {
  if (body == null || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const m = o.message ?? o.Message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

function couponValidityIndicatesFailure(body: unknown): boolean {
  if (body == null || typeof body !== "object") return false;
  const o = body as Record<string, unknown>;
  return o.isSuccess === false || o.success === false || o.IsSuccess === false;
}

function readCouponValidityData(body: unknown): Record<string, unknown> | null {
  if (body == null || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  const d = o.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    return d as Record<string, unknown>;
  }
  // Some environments return coupon DTO directly (no envelope).
  return o;
}

function readCouponDiscountValue(body: unknown): number {
  const d = readCouponValidityData(body);
  if (!d) return 0;
  const raw =
    d.discountvalue ?? d.discountValue ?? d.discount_amount ?? d.discountAmount;
  const n =
    typeof raw === "number" ? raw : Number.parseFloat(String(raw ?? ""));
  return Number.isFinite(n) ? n : 0;
}

function readCouponDisplayName(body: unknown): string {
  const d = readCouponValidityData(body);
  if (!d) return "";
  const raw =
    d.couponname ??
    d.couponName ??
    d.codename ??
    d.codeName ??
    d.qrcodelink ??
    d.qrCodeLink;
  return String(raw ?? "").trim();
}

type StepOptionId = string | number | null;

export type PlanGstDetailsPayload = {
  plangstdetailsid: number;
  planid: number;
  bookingmasterid: number;
  plangstamount: number;
  paneltygstamount: number;
  additinalgstamount: number;
  cashgstamount: number;
  securitydepositegstamount: number;
  processinggstamount: number;
  plangstpercentage: number;
  penaltygstpercentage: number;
  additionalgstpercentage: number;
  cashgstpercentage: number;
  depoistegstpercentage: number;
  processinggstpercentage: number;
};

function pickPlanGstNumber(
  source: Record<string, unknown>,
  keys: string[],
): number {
  for (const key of keys) {
    if (!(key in source)) continue;
    const v = source[key];
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function gstAmountFromPctOrAmount(
  amount: number,
  percentage: number,
  base: number,
): number {
  if (amount > 0) return amount;
  if (percentage > 0 && base > 0) return (base * percentage) / 100;
  return 0;
}

/** Maps hub plan GST fields into CreateBooking `bookingPlanModel.planGstDetails`. */
export function buildPlanGstDetailsPayload(
  selectedPlan: unknown,
  planIdNum: number,
  ctx: {
    planprice: number;
    processingFees: number;
    refundableAmount: number;
    additionalFees: number;
    cashCharge: number;
  },
): PlanGstDetailsPayload {
  const plan =
    selectedPlan != null && typeof selectedPlan === "object"
      ? (selectedPlan as Record<string, unknown>)
      : {};
  const nestedRaw =
    plan.planGstDetails ??
    plan.planGstDetails ??
    plan.plangstdetails ??
    plan.PlanGstDetails;
  const src =
    nestedRaw != null &&
    typeof nestedRaw === "object" &&
    !Array.isArray(nestedRaw)
      ? (nestedRaw as Record<string, unknown>)
      : plan;

  const plangstpercentage = pickPlanGstNumber(src, [
    "plangstpercentage",
    "planGstPercentage",
    "plan_gst_percentage",
  ]);
  const processinggstpercentage = pickPlanGstNumber(src, [
    "processinggstpercentage",
    "processingGstPercentage",
  ]);
  const depoistegstpercentage = pickPlanGstNumber(src, [
    "depoistegstpercentage",
    "depositegstpercentage",
    "depositGstPercentage",
    "securityDepositeGstPercentage",
  ]);
  const additionalgstpercentage = pickPlanGstNumber(src, [
    "additionalgstpercentage",
    "additionalGstPercentage",
  ]);
  const cashgstpercentage = pickPlanGstNumber(src, [
    "cashgstpercentage",
    "cashGstPercentage",
  ]);
  const penaltygstpercentage = pickPlanGstNumber(src, [
    "penaltygstpercentage",
    "penaltyGstPercentage",
  ]);

  return {
    plangstdetailsid: pickPlanGstNumber(src, [
      "plangstdetailsid",
      "planGstDetailsId",
      "PlanGstDetailsId",
    ]),
    planid: planIdNum,
    bookingmasterid: 0,
    plangstamount: gstAmountFromPctOrAmount(
      pickPlanGstNumber(src, [
        "plangstamount",
        "planGstAmount",
        "PlanGstAmount",
      ]),
      plangstpercentage,
      ctx.planprice,
    ),
    paneltygstamount: pickPlanGstNumber(src, [
      "paneltygstamount",
      "penaltyGstAmount",
      "penaltygstamount",
    ]),
    additinalgstamount: gstAmountFromPctOrAmount(
      pickPlanGstNumber(src, [
        "additinalgstamount",
        "additionalGstAmount",
        "additionalgstamount",
      ]),
      additionalgstpercentage,
      ctx.additionalFees,
    ),
    cashgstamount: gstAmountFromPctOrAmount(
      pickPlanGstNumber(src, [
        "cashgstamount",
        "cashGstAmount",
        "CashGstAmount",
      ]),
      cashgstpercentage,
      ctx.cashCharge,
    ),
    securitydepositegstamount: gstAmountFromPctOrAmount(
      pickPlanGstNumber(src, [
        "securitydepositegstamount",
        "securityDepositeGstAmount",
        "securityDepositGstAmount",
      ]),
      depoistegstpercentage,
      ctx.refundableAmount,
    ),
    processinggstamount: gstAmountFromPctOrAmount(
      pickPlanGstNumber(src, [
        "processinggstamount",
        "processingGstAmount",
        "ProcessingGstAmount",
      ]),
      processinggstpercentage,
      ctx.processingFees,
    ),
    plangstpercentage,
    penaltygstpercentage,
    additionalgstpercentage,
    cashgstpercentage,
    depoistegstpercentage,
    processinggstpercentage,
  };
}

export interface CreateBookingWizardProps {
  customerUserId: number;
  customerName: string;
  customerPhone: string;
  /** Step 0 Back returns to customer selection on the parent page */
  onBack: () => void;
}

function extractCreatedBookingId(res: unknown): string {
  const r = res as { data?: unknown; result?: unknown } | null | undefined;
  if (r && typeof r === "object") {
    const top = r as Record<string, unknown>;
    const topId =
      top.bookingmasterid ??
      top.bookingMasterId ??
      top.bookingid ??
      top.bookingId;
    if (topId != null && topId !== "") return String(topId);
  }
  const raw =
    (r && typeof r === "object"
      ? ((r as { data?: unknown }).data ?? (r as { result?: unknown }).result)
      : undefined) ?? r;
  const single = Array.isArray(raw) ? raw[0] : raw;
  if (typeof single === "number" && Number.isFinite(single))
    return String(single);
  if (typeof single === "string" && single.trim() !== "") return single.trim();
  if (!single || typeof single !== "object") return "";
  const o = single as Record<string, unknown>;
  const id =
    o.bookingmasterid ?? o.bookingMasterId ?? o.bookingid ?? o.bookingId;
  return id != null && id !== "" ? String(id) : "";
}

function extractCreatedBookingPlanId(res: unknown): number | null {
  const r = res as { data?: unknown; result?: unknown } | null | undefined;
  if (r && typeof r === "object") {
    const top = r as Record<string, unknown>;
    const topPlan =
      top.bookingplanid ?? top.bookingPlanId ?? top.planid ?? top.planId;
    if (topPlan != null && topPlan !== "") {
      const n = Number(topPlan);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  const raw =
    (r && typeof r === "object"
      ? ((r as { data?: unknown }).data ?? (r as { result?: unknown }).result)
      : undefined) ?? r;
  const single = Array.isArray(raw) ? raw[0] : raw;
  if (!single || typeof single !== "object") return null;
  const o = single as Record<string, unknown>;
  const planId = o.bookingplanid ?? o.bookingPlanId ?? o.planid ?? o.planId;
  if (planId == null || planId === "") return null;
  const n = Number(planId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveBookingPlanIdForPayment(createRes: unknown): number {
  const fromApi = extractCreatedBookingPlanId(createRes);
  if (fromApi != null) return fromApi;

  return 0;
}

/** Green success toast: use API `message` only (no static copy). */
function extractCreateBookingSuccessMessage(res: unknown): string | null {
  if (res == null || typeof res !== "object") return null;
  const m = (res as { message?: unknown }).message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

function pickVerifyPaymentResponseMessage(
  res: Record<string, unknown>,
): string | null {
  const m = res.message ?? res.Message;
  if (typeof m === "string" && m.trim() !== "") return m.trim();
  return null;
}

/** Backend may return order id under `data.id`, `data.orderId`, or top-level `id`. */
function extractRazorpayOrderId(orderRes: unknown): string | undefined {
  if (orderRes == null || typeof orderRes !== "object") return undefined;
  const o = orderRes as Record<string, unknown>;
  const nested = o.data;
  if (nested != null && typeof nested === "object") {
    const d = nested as Record<string, unknown>;
    const id = d.id ?? d.orderId ?? d.order_id;
    if (typeof id === "string" && id.trim() !== "") return id.trim();
  }
  const top = o.id ?? o.orderId ?? o.order_id;
  if (typeof top === "string" && top.trim() !== "") return top.trim();
  return undefined;
}

function extractPaymentLinkFromCollectionResponse(res: unknown): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  if (node == null || typeof node !== "object") return "";
  const o = node as Record<string, unknown>;

  const readLink = (obj: Record<string, unknown>): string => {
    const link =
      obj.paymentLink ??
      obj.payment_link ??
      obj.short_url ??
      obj.shortUrl ??
      obj.url;
    return typeof link === "string" && link.trim() !== "" ? link.trim() : "";
  };

  const direct = readLink(o);
  if (direct) return direct;

  const attrs = o.Attributes ?? o.attributes;
  if (attrs != null && typeof attrs === "object") {
    return readLink(attrs as Record<string, unknown>);
  }
  return "";
}

function extractQrImageSrcFromCollectionResponse(res: unknown): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  if (node == null || typeof node !== "object") return "";
  const o = node as Record<string, unknown>;

  const readImg = (obj: Record<string, unknown>): string => {
    const img =
      obj.image ??
      obj.qrImage ??
      obj.qr_image ??
      obj.imageUrl ??
      obj.image_url ??
      obj.qr_code_image ??
      obj.qrcode;
    if (typeof img !== "string" || img.trim() === "") return "";
    const s = img.trim();
    if (
      s.startsWith("data:") ||
      s.startsWith("http://") ||
      s.startsWith("https://")
    )
      return s;
    return `data:image/png;base64,${s}`;
  };

  const direct = readImg(o);
  if (direct) return direct;

  const attrs = o.Attributes ?? o.attributes;
  if (attrs != null && typeof attrs === "object") {
    return readImg(attrs as Record<string, unknown>);
  }
  return "";
}

/** Collection entity id (e.g. plink_*, qr id) after CreatePaymentLink / CreateQRCode; fallback e.g. Razorpay order id for transactionid. */
function extractPaymentIdFromCollectionResponse(
  res: unknown,
  fallback: string,
): string {
  let node: unknown = res;
  if (node != null && typeof node === "object" && "data" in (node as object)) {
    const inner = (node as { data: unknown }).data;
    if (inner != null && typeof inner === "object") node = inner;
  }
  const readId = (obj: Record<string, unknown>): string | null => {
    for (const k of [
      "paymentId",
      "paymentid",
      "PaymentId",
      "payment_id",
      "qrId",
      "qr_id",
      "qrcode_id",
    ]) {
      const v = obj[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    const id = obj.id;
    if (id != null && String(id).trim() !== "") return String(id).trim();
    return null;
  };
  if (node != null && typeof node === "object") {
    const o = node as Record<string, unknown>;
    const direct = readId(o);
    if (direct) return direct;
    /** Create QR response: `{ data: { Attributes: { id: "qr_..." } } }` */
    const attrs = o.Attributes ?? o.attributes;
    if (attrs != null && typeof attrs === "object") {
      const nested = readId(attrs as Record<string, unknown>);
      if (nested) return nested;
    }
  }
  return fallback;
}

function readCreateWizardStep(customerUserId: number): number {
  try {
    if (
      String(
        sessionStorage.getItem(CREATE_BOOKING_SK.wizardCustomerUserId) ?? "",
      ) !== String(customerUserId)
    )
      return 0;
    const saved = sessionStorage.getItem(CREATE_BOOKING_SK.step);
    if (saved != null) {
      const step = Number.parseInt(saved, 10);
      if (step >= 0 && step < 4) return step;
    }
  } catch {
    // ignore
  }
  return 0;
}

function readCreateWizardHubId(customerUserId: number): number | null {
  try {
    if (
      String(
        sessionStorage.getItem(CREATE_BOOKING_SK.wizardCustomerUserId) ?? "",
      ) !== String(customerUserId)
    )
      return null;
    const saved = sessionStorage.getItem(CREATE_BOOKING_SK.hubId);
    if (saved != null && saved !== "") {
      const n = Number.parseInt(saved, 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch {
    // ignore
  }
  return null;
}

function readCreateWizardStepOptionId(
  customerUserId: number,
  key: string,
): StepOptionId {
  try {
    if (
      String(
        sessionStorage.getItem(CREATE_BOOKING_SK.wizardCustomerUserId) ?? "",
      ) !== String(customerUserId)
    )
      return null;
    const saved = sessionStorage.getItem(key);
    if (saved != null && saved !== "") return saved;
  } catch {
    // ignore
  }
  return null;
}

const CreateBookingWizard: React.FC<CreateBookingWizardProps> = ({
  customerUserId,
  customerName: customerNameProp,
  customerPhone: phoneNumberProp,
  onBack,
}) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAppSelector((state: RootState) => state.auth);

  /** Booking list route — `replace` avoids stacking create-booking in history after success. */
  const navigateToBookingManagementAfterSuccess = useCallback(() => {
    clearCreateBookingDraft();
    navigate("/booking-management", {
      replace: true,
      state: { shouldRefetch: true },
    });
  }, [navigate]);

  const [activeStep, setActiveStep] = useState(() =>
    readCreateWizardStep(customerUserId),
  );
  const [selectedHubId, setSelectedHubId] = useState<number | null>(() =>
    readCreateWizardHubId(customerUserId),
  );
  const [selectedModelId, setSelectedModelId] = useState<StepOptionId>(() =>
    readCreateWizardStepOptionId(customerUserId, CREATE_BOOKING_SK.modelId),
  );
  const [selectedVehicleId, setSelectedVehicleId] = useState<StepOptionId>(() =>
    readCreateWizardStepOptionId(customerUserId, CREATE_BOOKING_SK.vehicleId),
  );
  const [remarks, setRemarks] = useState("");
  const bookingDetails: any = null;
  const tripStatusFromApi = undefined;
  const [vehiclePhotos, setVehiclePhotos] = useState<string[]>([]);
  const [batteryPhotos, setBatteryPhotos] = useState<string[]>([]);
  const [chargerPhotos, setChargerPhotos] = useState<string[]>([]);
  const [
    vehicleConditionChecklistMappings,
    setVehicleConditionChecklistMappings,
  ] = useState<Record<string, boolean>>({});
  const [isEditingOldChecklist, setIsEditingOldChecklist] = useState(false);
  const [newVehiclePhotos, setNewVehiclePhotos] = useState<string[]>([]);
  const [newVehicleBatteryPhotos, setNewVehicleBatteryPhotos] = useState<
    string[]
  >([]);
  const [newVehicleChargerPhotos, setNewVehicleChargerPhotos] = useState<
    string[]
  >([]);
  const [newVehicleChecklistMappings, setNewVehicleChecklistMappings] =
    useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const [showVehicleUploadValidation, setShowVehicleUploadValidation] =
    useState(false);
  const [customerKycState, setCustomerKycState] = useState<CustomerKycState>({
    loading: false,
    checked: false,
    verified: true,
    hasDlOcrFullname: true,
    hasKycRecord: true,
    missingMessage: null,
  });

  const [couponCode, setCouponCode] = useState("");
  const [promoCodeOptions, setPromoCodeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [promoCodeMetaByValue, setPromoCodeMetaByValue] = useState<
    Record<
      string,
      { couponid: number; couponName: string; isReferralCoupon: boolean }
    >
  >({});
  const [promoCodesLoading, setPromoCodesLoading] = useState(false);
  const [couponApplyLoading, setCouponApplyLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    couponid: number;
    name: string;
    discountValue: number;
    isReferralCoupon: boolean;
  } | null>(null);
  const lastCouponValidityKeyRef = useRef("");
  const couponPlanRevalidateFlightRef = useRef(0);
  const [additionalFees, setAdditionalFees] = useState("");
  const [onboardingProcessingFees, setOnboardingProcessingFees] = useState("0");
  const [onboardingDepositAmount, setOnboardingDepositAmount] = useState("0");
  const [onboardingFeeRemarks, setOnboardingFeeRemarks] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<"cash" | "online">(
    "online",
  );
  const [selectedPlanId, setSelectedPlanId] = useState<StepOptionId>(() =>
    readCreateWizardStepOptionId(customerUserId, CREATE_BOOKING_SK.planId),
  );
  const [costBreakdown, setCostBreakdown] = useState<CostBreakdownItem[]>([]);
  const [costBreakdownRaw, setCostBreakdownRaw] = useState<{
    planprice?: number;
    processing_fees?: number;
    refundable_amount?: number;
  }>({});
  const [costBreakdownLoading, setCostBreakdownLoading] = useState(false);
  const [totalPayable, setTotalPayable] = useState("₹0.00");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [postSavePaymentMode, setPostSavePaymentMode] = useState<
    "payment_link" | "qr_code"
  >("payment_link");
  const [onlineCollectionDialogOpen, setOnlineCollectionDialogOpen] =
    useState(false);
  const [onlineCollectionLoading, setOnlineCollectionLoading] = useState(false);
  const [onlineCollectionError, setOnlineCollectionError] = useState<
    string | null
  >(null);
  const [onlineCollectionPaymentLink, setOnlineCollectionPaymentLink] =
    useState("");
  const [onlineCollectionQrSrc, setOnlineCollectionQrSrc] = useState("");
  const [onlineCollectionOrderId, setOnlineCollectionOrderId] = useState("");
  /** From create QR / payment link response (`data.Attributes.id`) for verify `qrId`. */
  const [onlineCollectionVerifyEntityId, setOnlineCollectionVerifyEntityId] =
    useState<string | null>(null);
  const [verifyPaymentLoading, setVerifyPaymentLoading] = useState(false);
  const [onlineCollectionBookingDone, setOnlineCollectionBookingDone] =
    useState(false);
  const verifyPaymentPollAbortRef = useRef(false);
  const onlineCollectionBookingDoneRef = useRef(false);
  const [vehicleUnderbookingLoading, setVehicleUnderbookingLoading] =
    useState(false);
  const [cashPaymentCharge, setCashPaymentCharge] = useState<string>("");

  useEffect(() => {
    onlineCollectionBookingDoneRef.current = onlineCollectionBookingDone;
  }, [onlineCollectionBookingDone]);

  const displayTotalPayable = useMemo(() => {
    const base =
      Number.parseFloat(String(totalPayable).replaceAll(/[₹,\s]/g, "")) || 0;
    let sum = base;
    if (paymentMethod === "cash" && cashPaymentCharge !== "") {
      sum += Number(cashPaymentCharge) || 0;
    }
    const addStr = additionalFees.trim();
    if (addStr !== "") {
      const add = Number.parseFloat(addStr);
      if (Number.isFinite(add)) sum += add;
    }
    const disc = appliedCoupon?.discountValue ?? 0;
    if (Number.isFinite(disc) && disc > 0) sum -= disc;
    const safe = Number.isFinite(sum) ? Math.max(0, sum) : 0;
    return `₹${safe.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
  }, [
    totalPayable,
    paymentMethod,
    cashPaymentCharge,
    additionalFees,
    appliedCoupon?.discountValue,
  ]);

  const { hubs, loading: hubsLoading } = useHubs(setError);

  const { hasAnyRole } = useRBAC();
  const relations = getRoleHubRelations();

  const isHubOrClusterRole = hasAnyRole([
    ROLES.HUB_MANAGER,
    ROLES.HUB_CLUSTER_MANAGER,
  ]);
  const allowedHubIdsForEdit = useMemo(() => {
    if (!isHubOrClusterRole || !relations?.length) return null;
    return new Set(relations.map((r) => r.hubid));
  }, [isHubOrClusterRole, relations]);

  const displayHubs = useMemo(() => {
    if (allowedHubIdsForEdit == null) return hubs ?? [];
    return (hubs ?? []).filter((h) =>
      allowedHubIdsForEdit.has(Number(h.hubmasterid)),
    );
  }, [hubs, allowedHubIdsForEdit]);

  /** Name of the hub selected in step 0 — shown on Select Vehicle Model step */
  const selectedHubDisplayName = useMemo(() => {
    if (selectedHubId == null) return "";
    const fromDisplay = displayHubs.find(
      (h) => Number(h.hubmasterid) === Number(selectedHubId),
    );
    if (fromDisplay?.hubname) return String(fromDisplay.hubname);
    const fromAll = (hubs ?? []).find(
      (h) => Number(h.hubmasterid) === Number(selectedHubId),
    );
    if (fromAll?.hubname) return String(fromAll.hubname);
    return "";
  }, [selectedHubId, displayHubs, hubs]);

  const {
    statusCodes: vehicleModelCodes,
    getDisplayNameByCodeValue: getVehicleModelDisplayName,
  } = useCodeCommonByCategory("VehicleModel");
  const {
    statusCodes: evSpeedTypeCodes,
    getDisplayNameByCodeValue: getSpeedTypeDisplayName,
  } = useCodeCommonByCategory("EVSpeedType");

  const isHubVehicleHighSpeed = useCallback(
    (v: any) => {
      if (v == null) return false;
      const st = v.speedtype ?? v.speedType;
      const label = String(getSpeedTypeDisplayName(st) ?? "").toLowerCase();
      return label.includes("high");
    },
    [getSpeedTypeDisplayName],
  );

  const { statusCodes: paymentmentCodes } =
    useCodeCommonByCategory("PaymentMethod");
  const [hubVehicles, setHubVehicles] = useState<any[]>([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [plansData, setPlansData] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  const customerName = (customerNameProp ?? "").trim() || "—";
  const phoneNumber = (phoneNumberProp ?? "").trim() || "—";

  useEffect(() => {
    try {
      sessionStorage.setItem(
        CREATE_BOOKING_SK.wizardCustomerUserId,
        String(customerUserId),
      );
    } catch {
      // ignore
    }
  }, [customerUserId]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CREATE_BOOKING_SK.step, String(activeStep));
    } catch {
      // ignore
    }
  }, [activeStep]);

  useEffect(() => {
    try {
      if (selectedHubId === null)
        sessionStorage.removeItem(CREATE_BOOKING_SK.hubId);
      else
        sessionStorage.setItem(CREATE_BOOKING_SK.hubId, String(selectedHubId));
    } catch {
      // ignore
    }
  }, [selectedHubId]);

  useEffect(() => {
    try {
      if (selectedModelId === null || selectedModelId === "")
        sessionStorage.removeItem(CREATE_BOOKING_SK.modelId);
      else
        sessionStorage.setItem(
          CREATE_BOOKING_SK.modelId,
          String(selectedModelId),
        );
    } catch {
      // ignore
    }
  }, [selectedModelId]);

  useEffect(() => {
    try {
      if (selectedVehicleId === null || selectedVehicleId === "")
        sessionStorage.removeItem(CREATE_BOOKING_SK.vehicleId);
      else
        sessionStorage.setItem(
          CREATE_BOOKING_SK.vehicleId,
          String(selectedVehicleId),
        );
    } catch {
      // ignore
    }
  }, [selectedVehicleId]);

  useEffect(() => {
    try {
      if (selectedPlanId === null || selectedPlanId === "")
        sessionStorage.removeItem(CREATE_BOOKING_SK.planId);
      else
        sessionStorage.setItem(
          CREATE_BOOKING_SK.planId,
          String(selectedPlanId),
        );
    } catch {
      // ignore
    }
  }, [selectedPlanId]);

  useEffect(() => {
    const userIdNum = Number(customerUserId);
    if (!Number.isFinite(userIdNum) || userIdNum <= 0) {
      setCustomerKycState({
        loading: false,
        checked: false,
        verified: true,
        hasDlOcrFullname: true,
        hasKycRecord: true,
        missingMessage: null,
      });
      return;
    }
    let cancelled = false;
    setCustomerKycState({
      loading: true,
      checked: false,
      verified: true,
      hasDlOcrFullname: true,
      hasKycRecord: true,
      missingMessage: null,
    });
    apiService
      .getKYCByUserIdForCreateBooking(userIdNum)
      .then((res: unknown) => {
        if (cancelled) return;
        const wrap = res as { isSuccess?: boolean; message?: string };
        if (wrap && typeof wrap === "object" && wrap.isSuccess === false) {
          const apiMsg =
            typeof wrap.message === "string" ? wrap.message.trim() : "";
          setCustomerKycState({
            loading: false,
            checked: true,
            verified: false,
            hasDlOcrFullname: false,
            hasKycRecord: false,
            missingMessage:
              apiMsg !== "" ? apiMsg : KYC_NOT_FOUND_DEFAULT_MESSAGE,
          });
          return;
        }
        const list = pickKycListFromResponse(res);
        const hasKycRecord = list.length > 0;
        const primary =
          list.find((k: any) => Number(k?.kycstatus) === 1) ??
          list[list.length - 1];
        const statusNum =
          primary != null ? Number(primary.kycstatus) : Number.NaN;
        const verified = statusNum === 1;
        const hasDlOcrFullname = kycDlOcrFullnamePresent(primary);
        setCustomerKycState({
          loading: false,
          checked: true,
          verified,
          hasDlOcrFullname,
          hasKycRecord,
          missingMessage: hasKycRecord ? null : KYC_NOT_FOUND_DEFAULT_MESSAGE,
        });
      })
      .catch(() => {
        if (!cancelled)
          setCustomerKycState({
            loading: false,
            checked: true,
            verified: false,
            hasDlOcrFullname: false,
            hasKycRecord: false,
            missingMessage: "Failed to load KYC.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [customerUserId]);

  useEffect(() => {
    if (!customerKycState.checked || customerKycState.loading) return;
    if (selectedVehicleId == null) return;
    const v = hubVehicles.find(
      (x: any) => String(x.vehicleid ?? x.id) === String(selectedVehicleId),
    );
    if (!v) return;
    const allowsOnePerson = customerKycState.verified;
    const allowsHighSpeed =
      customerKycState.verified && customerKycState.hasDlOcrFullname;
    const onePersonBlocked = Number(v.vehicletype) === 1 && !allowsOnePerson;
    const highSpeedBlocked = isHubVehicleHighSpeed(v) && !allowsHighSpeed;
    if (onePersonBlocked || highSpeedBlocked) setSelectedVehicleId(null);
  }, [
    customerKycState.checked,
    customerKycState.loading,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedVehicleId,
    hubVehicles,
    isHubVehicleHighSpeed,
  ]);

  useEffect(() => {
    if (displayHubs?.length && selectedHubId !== null) {
      const existsInHubs = displayHubs.some(
        (h) => Number(h.hubmasterid) === Number(selectedHubId),
      );
      if (existsInHubs) return;
      setSelectedHubId(null);
    }
  }, [displayHubs, selectedHubId]);

  const handleCancel = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  const fetchVehiclesForHub = async (hubId: number) => {
    setVehiclesLoading(true);
    try {
      const res = await apiService.getVehiclesByHubId({
        hubmasterid: hubId,
        pageNumber: 1,
        pageSize: 100,
      });
      const { items } = parseSearchPaymentDetailsResponse(res);
      setHubVehicles(Array.isArray(items) ? items : []);
    } catch {
      setHubVehicles([]);
    } finally {
      setVehiclesLoading(false);
    }
  };

  const fetchPlansForHub = async (hubId: number) => {
    setPlansLoading(true);
    try {
      const res: any = await apiService.getPlansByHubId(String(hubId));
      const data = res?.data || res;
      setPlansData(Array.isArray(data) ? data : []);
    } catch {
      setPlansData([]);
    } finally {
      setPlansLoading(false);
    }
  };

  /** Load hub vehicles/plans whenever hub is known so booking vehicle & model can pre-select without advancing steps or refreshing */
  useEffect(() => {
    if (selectedHubId === null) return;
    fetchVehiclesForHub(selectedHubId);
    fetchPlansForHub(selectedHubId);
  }, [selectedHubId]);

  /**
   * ChangeVehicleStatusToUnderbooking — when API returns `status: 7`, vehicle is not available (e.g. already under booking).
   * Show the server `message` via NotificationContext (not a static string).
   */
  const reserveVehicleForUnderbooking = useCallback(
    async (
      vehicleId: number,
      vehicle: Record<string, unknown>,
    ): Promise<boolean> => {
      const rawStatus =
        vehicle.status ?? vehicle.vehiclestatus ?? vehicle.vehicleStatus;
      if (rawStatus == null || rawStatus === "") return true;
      try {
        const res = (await apiService.updateVehicleStatus(
          vehicleId,
          Number(rawStatus),
        )) as Record<string, unknown> | null | undefined;
        const inner =
          res != null &&
          typeof res === "object" &&
          res.data != null &&
          typeof res.data === "object" &&
          !Array.isArray(res.data) &&
          ("status" in (res.data as object) ||
            "message" in (res.data as object))
            ? (res.data as { status?: unknown; message?: unknown })
            : res;
        if (
          inner != null &&
          typeof inner === "object" &&
          Number(inner.status) === 7
        ) {
          const msg =
            typeof inner.message === "string" ? inner.message.trim() : "";
          showNotification(msg);
          return false;
        }
        return true;
      } catch (e: unknown) {
        const fallback =
          e instanceof Error && e.message
            ? e.message
            : "Unable to reserve this vehicle.";
        showNotification(fallback, "error");
        return false;
      }
    },
    [showNotification],
  );

  const handleVehicleStepSelect = useCallback((id: string | number) => {
    setSelectedVehicleId(id);
  }, []);

  const {
    hasUpdateSection,
    hasNewVehicleSection,
    vehicleStepValid,
    originalVehicleType,
    selectedVehicleType,
    kycSelectionInvalid,
    kycBlocksOnePersonVehicles,
    kycBlocksHighSpeedVehicles,
    kycVehicleStepBlockMessage,
  } = useMemo(() => {
    const b = bookingDetails || {};
    const hasUpdate = tripStatusFromApi === 5;
    const hasTripStartData = Boolean(
      b.tripStart &&
      ((Array.isArray(b.tripStart.vehicleconditionchecklistmappings) &&
        b.tripStart.vehicleconditionchecklistmappings.length > 0) ||
        b.tripStart.vehiclephotos ||
        b.tripStart.batteryphotos ||
        b.tripStart.chargerphotos),
    );
    const hasUpdateChecklistSection = hasUpdate && hasTripStartData;
    const hasNew =
      selectedVehicleId != null &&
      b.vehicleid != null &&
      Number(selectedVehicleId) !== Number(b.vehicleid);
    /** Upload validation for new vehicle checklist only when trip status is 5 (same as Update checklist UI). */
    const newVehicleUploadRequired = hasNew && tripStatusFromApi === 5;
    const originalVehicle = hubVehicles?.find(
      (v: any) => Number(v.vehicleid ?? v.id) === Number(b.vehicleid),
    );
    const selectedVehicle = hubVehicles?.find(
      (v: any) => String(v.vehicleid ?? v.id) === String(selectedVehicleId),
    );
    const origType = originalVehicle?.vehicletype ?? null;
    const selType = selectedVehicle?.vehicletype ?? null;
    const hasUpdateChecklistSelection = Object.values(
      vehicleConditionChecklistMappings,
    ).includes(true);
    const hasNewChecklistSelection = Object.values(
      newVehicleChecklistMappings,
    ).includes(true);
    const updateOk =
      !hasUpdate ||
      !hasUpdateChecklistSection ||
      (hasUpdateChecklistSelection &&
        vehiclePhotos.length > 0 &&
        (origType !== 1 ||
          (batteryPhotos.length > 0 && chargerPhotos.length > 0)));
    const newOk =
      !newVehicleUploadRequired ||
      (hasNewChecklistSelection &&
        newVehiclePhotos.length > 0 &&
        (selType !== 1 ||
          (newVehicleBatteryPhotos.length > 0 &&
            newVehicleChargerPhotos.length > 0)));
    const selectedSpeedLabel =
      selectedVehicle != null
        ? String(
            getSpeedTypeDisplayName(
              selectedVehicle.speedtype ?? selectedVehicle.speedType,
            ) ?? "",
          ).toLowerCase()
        : "";
    const selectedIsHighSpeed = selectedSpeedLabel.includes("high");
    const kycBlocksOnePersonVehicles =
      customerKycState.checked && !customerKycState.verified;
    const kycBlocksHighSpeedVehicles =
      customerKycState.checked &&
      (!customerKycState.verified || !customerKycState.hasDlOcrFullname);
    const kycOnePersonInvalid =
      !customerKycState.loading &&
      kycBlocksOnePersonVehicles &&
      selectedVehicle != null &&
      Number(selectedVehicle?.vehicletype) === 1;
    const kycHighSpeedInvalid =
      !customerKycState.loading &&
      kycBlocksHighSpeedVehicles &&
      selectedVehicle != null &&
      selectedIsHighSpeed;
    const kycSelectionInvalid = kycOnePersonInvalid || kycHighSpeedInvalid;
    const kycVehicleStepBlockMessage = kycHighSpeedInvalid
      ? KYC_HIGH_SPEED_DL_MESSAGE
      : kycOnePersonInvalid
        ? KYC_INCOMPLETE_VEHICLE_MESSAGE
        : null;
    const valid = updateOk && newOk && !kycSelectionInvalid;
    return {
      hasUpdateSection: hasUpdateChecklistSection,
      hasNewVehicleSection: hasNew,
      vehicleStepValid: valid,
      originalVehicleType: origType,
      selectedVehicleType: selType,
      kycSelectionInvalid,
      kycBlocksOnePersonVehicles,
      kycBlocksHighSpeedVehicles,
      kycVehicleStepBlockMessage,
    };
  }, [
    bookingDetails,
    tripStatusFromApi,
    selectedVehicleId,
    hubVehicles,
    customerKycState.checked,
    customerKycState.verified,
    customerKycState.loading,
    customerKycState.hasDlOcrFullname,
    getSpeedTypeDisplayName,
    vehiclePhotos.length,
    batteryPhotos.length,
    chargerPhotos.length,
    vehicleConditionChecklistMappings,
    newVehiclePhotos.length,
    newVehicleBatteryPhotos.length,
    newVehicleChargerPhotos.length,
    newVehicleChecklistMappings,
  ]);

  useEffect(() => {
    if (activeStep !== 2) setShowVehicleUploadValidation(false);
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === 2 && vehicleStepValid) {
      setShowVehicleUploadValidation(false);
      if (error != null) setError(null);
    }
  }, [activeStep, vehicleStepValid, error]);

  const handleHubSelect = (hubId: number) => {
    setSelectedHubId(hubId);
  };

  const handleNavigate = (hub: {
    hubname: string;
    hubmasterid: number;
    latitude?: number;
    longitude?: number;
  }) => {
    if (hub.latitude != null && hub.longitude != null) {
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${hub.latitude},${hub.longitude}`,
        "_blank",
      );
    } else {
      const query = encodeURIComponent(hub.hubname);
      window.open(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
        "_blank",
      );
    }
  };

  const hubCards = useMemo(() => {
    return displayHubs.map((hub) => {
      const addressParts = [hub.address1, hub.address2, hub.zipcode].filter(
        Boolean,
      );
      const address = addressParts.length > 0 ? addressParts.join(", ") : "—";
      const active = Number(hub.activeVehicleCount ?? 0);
      const available = Number(hub.availableVehicleCount ?? 0);
      const totalVehicles = Number(hub.totalvehiclesCount ?? 0);
      const readyOrListed = active + available;
      const availableText =
        totalVehicles > 0
          ? `${readyOrListed}/${totalVehicles} Available`
          : `${readyOrListed} Available`;
      const rawPath = (hub.hubimage || "").trim().replace(/^\/+/, "");
      const imageUrl = rawPath ? `${BASE_IMAGE_URL}${rawPath}` : "";
      return {
        hubmasterid: hub.hubmasterid,
        hubname: hub.hubname,
        address,
        operationhours: hub.operationhours || "—",
        available: availableText,
        imageUrl,
        latitude: hub.latitude,
        longitude: hub.longitude,
      };
    });
  }, [displayHubs]);

  const vehicleModelOptions: VehicleModelOption[] = useMemo(() => {
    if (!hubVehicles?.length) return [];
    const vehicleTypeToSetting = (
      vehicletype: number | null | undefined,
    ): string => {
      if (vehicletype === 1) return "1 Person";
      if (vehicletype === 2) return "2 Person";
      return "—";
    };
    const byModel = new Map<number, any[]>();
    for (const v of hubVehicles) {
      const modelId = v.vehiclemodel ?? v.vehiclemodelid;
      if (modelId == null) continue;
      const key = Number(modelId);
      if (!byModel.has(key)) byModel.set(key, []);
      byModel.get(key)!.push(v);
    }
    return Array.from(byModel.entries()).map(([modelId, vehicles]) => {
      const first = vehicles[0];
      const speedDisplay = getSpeedTypeDisplayName(first.speedtype);
      const speed = speedDisplay === "" ? "—" : speedDisplay;
      const speedTypeLabel = String(speedDisplay ?? "").toLowerCase();
      const isHighSpeed = speedTypeLabel.includes("high");
      const weight =
        first.weight != null && first.weight !== ""
          ? `${first.weight} KG`
          : "—";
      const setting = vehicleTypeToSetting(first.vehicletype);
      const count = vehicles.length;
      const available = count === 1 ? "1 Available" : `${count} Available`;
      const modelDisplayName = getVehicleModelDisplayName(
        first.vehiclemodel ?? modelId,
      );
      const name =
        modelDisplayName === ""
          ? first.vehiclename || `Model ${modelId}`
          : modelDisplayName;
      return {
        id: modelId,
        name,
        speed,
        weight,
        setting,
        available,
        imageUrl: pickVehicleCardImageFromApiField(
          readVehicleImagesJsonFromRow(first),
        ),
        isHighSpeed,
      };
    });
  }, [
    hubVehicles,
    vehicleModelCodes,
    evSpeedTypeCodes,
    getVehicleModelDisplayName,
    getSpeedTypeDisplayName,
  ]);

  const kycModelSelectionInvalid = useMemo(() => {
    if (customerKycState.loading) return false;
    const kycBlocksHighSpeedModels =
      customerKycState.checked &&
      (!customerKycState.verified || !customerKycState.hasDlOcrFullname);
    if (!kycBlocksHighSpeedModels) return false;
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    return opt?.isHighSpeed === true;
  }, [
    customerKycState.loading,
    customerKycState.checked,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedModelId,
    vehicleModelOptions,
  ]);

  useEffect(() => {
    if (!customerKycState.checked || customerKycState.loading) return;
    if (selectedModelId == null || selectedModelId === "") return;
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    if (opt?.isHighSpeed !== true) return;
    const allowsHighSpeed =
      customerKycState.verified && customerKycState.hasDlOcrFullname;
    if (!allowsHighSpeed) setSelectedModelId(null);
  }, [
    customerKycState.checked,
    customerKycState.loading,
    customerKycState.verified,
    customerKycState.hasDlOcrFullname,
    selectedModelId,
    vehicleModelOptions,
  ]);

  const handleNext = async () => {
    if (
      activeStep === 0 &&
      customerKycState.checked &&
      !customerKycState.hasKycRecord
    ) {
      setError(
        customerKycState.missingMessage ?? KYC_NOT_FOUND_DEFAULT_MESSAGE,
      );
      return;
    }
    if (activeStep === 1 && kycModelSelectionInvalid) {
      setError(KYC_HIGH_SPEED_DL_MESSAGE);
      return;
    }
    if (activeStep === 2 && !vehicleStepValid) {
      setShowVehicleUploadValidation(true);
      let message: string;
      const hasUpdateChecklistSelection = Object.values(
        vehicleConditionChecklistMappings,
      ).includes(true);
      const hasNewChecklistSelection = Object.values(
        newVehicleChecklistMappings,
      ).includes(true);
      if (kycSelectionInvalid) {
        message = kycVehicleStepBlockMessage ?? KYC_INCOMPLETE_VEHICLE_MESSAGE;
      } else if (hasUpdateSection && !hasUpdateChecklistSelection) {
        message =
          "Select at least one item in Update existing vehicle checklist before proceeding.";
      } else if (hasUpdateSection && vehiclePhotos.length === 0) {
        message =
          "Vehicle images are required. Please upload at least one vehicle image before proceeding.";
      } else if (
        hasUpdateSection &&
        originalVehicleType === 1 &&
        (batteryPhotos.length === 0 || chargerPhotos.length === 0)
      ) {
        message =
          "Battery and charger images are required for this vehicle. Please upload at least one of each before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        !hasNewChecklistSelection
      ) {
        message =
          "Select at least one item in New vehicle checklist before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        newVehiclePhotos.length === 0
      ) {
        message =
          "Vehicle images are required for the new vehicle. Please upload at least one vehicle image before proceeding.";
      } else if (
        tripStatusFromApi === 5 &&
        hasNewVehicleSection &&
        selectedVehicleType === 1 &&
        (newVehicleBatteryPhotos.length === 0 ||
          newVehicleChargerPhotos.length === 0)
      ) {
        message =
          "Battery and charger images are required for the new vehicle. Please upload at least one of each before proceeding.";
      } else {
        message =
          "Please upload all required vehicle images before proceeding.";
      }
      setError(message);
      return;
    }
    if (activeStep === 2) {
      const vehicleId = Number(selectedVehicleId);
      const vehicle = hubVehicles.find(
        (v: any) => Number(v.vehicleid ?? v.id) === vehicleId,
      ) as Record<string, unknown> | undefined;
      if (!Number.isFinite(vehicleId) || !vehicle) {
        setError("Select a vehicle before proceeding.");
        return;
      }
      setVehicleUnderbookingLoading(true);
      const ok = await reserveVehicleForUnderbooking(vehicleId, vehicle);
      setVehicleUnderbookingLoading(false);
      if (!ok) return;
    }
    if (activeStep === 2) setShowVehicleUploadValidation(false);
    setError(null);
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const selectedVehicleModelDisplayName = useMemo(() => {
    if (selectedModelId == null || selectedModelId === "") return "";
    const opt = vehicleModelOptions.find(
      (o) => String(o.id) === String(selectedModelId),
    );
    return (opt?.name ?? "").trim();
  }, [selectedModelId, vehicleModelOptions]);

  const vehicleOptions: VehicleOption[] = useMemo(() => {
    if (!hubVehicles.length) return [];
    const byModel =
      selectedModelId === null || selectedModelId === undefined
        ? hubVehicles
        : hubVehicles.filter(
            (v: any) =>
              String(v.vehiclemodel || v.vehiclemodelid) ===
              String(selectedModelId),
          );
    const allowedStatuses = [1, 5];
    const filtered = byModel.filter((v: any) => {
      const status = v.status ?? v.vehiclestatus ?? v.vehicleStatus;
      const num = status != null && status !== "" ? Number(status) : NaN;
      return Number.isFinite(num) && allowedStatuses.includes(num);
    });
    return filtered.map((v: any) => {
      const speedTypeLabel = String(
        getSpeedTypeDisplayName(v.speedtype ?? v.speedType) ?? "",
      ).toLowerCase();
      return {
        id: v.vehicleid ?? v.id,
        name: v.vehiclename,
        speed:
          v.topspeed === null || v.topspeed === undefined
            ? "—"
            : `${v.topspeed} KM/H`,
        range:
          v.rangeperchargekm === null || v.rangeperchargekm === undefined
            ? "—"
            : `${v.rangeperchargekm} KM`,
        imageUrl: pickVehicleCardImageFromApiField(
          readVehicleImagesJsonFromRow(v),
        ),
        vehicleNum: v.vehiclenum || "",
        registrationNumber: String(
          v.registrationnumber ??
            v.registrationNumber ??
            v.vechileistrationnumber ??
            v.vehicleRegistrationNumber ??
            "",
        ).trim(),
        vehicleType:
          v.vehicletype != null && v.vehicletype !== ""
            ? Number(v.vehicletype)
            : null,
        isHighSpeed: speedTypeLabel.includes("high"),
      };
    });
  }, [hubVehicles, selectedModelId, getSpeedTypeDisplayName]);

  /** Selected hub vehicle — shown under "Your selection" on plan step (empty → N/A in UI) */
  const yourSelectionVehicleDetails: YourSelectionVehicleDetailStrings =
    useMemo(() => {
      if (selectedVehicleId == null || selectedVehicleId === "") {
        return {
          vehicleName: "",
          vehicleNum: "",
          registrationNumber: "",
          speed: "",
          range: "",
        };
      }
      const v = vehicleOptions.find(
        (x) => String(x.id) === String(selectedVehicleId),
      );
      if (!v) {
        return {
          vehicleName: "",
          vehicleNum: "",
          registrationNumber: "",
          speed: "",
          range: "",
        };
      }
      return {
        vehicleName: String(v.name ?? "").trim(),
        vehicleNum: String(v.vehicleNum ?? "").trim(),
        registrationNumber: String(v.registrationNumber ?? "").trim(),
        speed: String(v.speed ?? "").trim(),
        range: String(v.range ?? "").trim(),
      };
    }, [selectedVehicleId, vehicleOptions]);

  const planOptions: PlanOption[] = useMemo(() => {
    if (selectedModelId == null || selectedModelId === "") return [];
    const filtered = plansData.filter((p: any) => {
      const planModelId = p.vehiclemodelid ?? p.vehiclemodel;
      if (
        planModelId === null ||
        planModelId === undefined ||
        planModelId === ""
      )
        return false;
      return String(planModelId) === String(selectedModelId);
    });
    return filtered.map((p: any) => ({
      id: p.planid ?? p.id,
      name: p.planname || " ",
      duration: p.days ? `${p.days} Days` : undefined,
      price:
        p.price === null || p.price === undefined
          ? undefined
          : `₹${Number(p.price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
      isCustom: false,
    }));
  }, [plansData, selectedModelId]);

  /**
   * Plan window sent in `bookingPlanUpdateModel` (today → inclusive end from selected plan `days`).
   * Reused on Select Plan step Your selection for "New plan start" / "New plan end".
   */
  const newPlanWindowForCreate = useMemo(() => {
    if (selectedPlanId == null || selectedPlanId === "") return null;
    const selectedPlan = plansData.find(
      (p: any) => String(p.planid ?? p.id) === String(selectedPlanId),
    );
    const rawDays = Number(selectedPlan?.days);
    const planDurationDays = Math.max(
      1,
      Number.isFinite(rawDays) && rawDays > 0 ? rawDays : 1,
    );
    const planStart = dayjs().startOf("day");
    let planEnd = planStart.add(planDurationDays - 1, "day").startOf("day");
    if (isIndianWallClockAfterTwoPm()) {
      planEnd = planEnd.add(1, "day").startOf("day");
    }
    return {
      planStartISO: planStart.toISOString(),
      planEndISO: planEnd.toISOString(),
      planStartDisplay: planStart.format("DD/MM/YYYY"),
      planEndDisplay: planEnd.format("DD/MM/YYYY"),
    };
  }, [selectedPlanId, plansData]);

  /** Clear plan selection if it does not belong to the selected vehicle model (after plans load) */
  useEffect(() => {
    if (selectedPlanId == null || selectedPlanId === "") return;
    if (plansLoading) return;
    const stillValid = planOptions.some(
      (p) => String(p.id) === String(selectedPlanId),
    );
    if (!stillValid) setSelectedPlanId(null);
  }, [planOptions, selectedPlanId, plansLoading]);

  /** New booking: derive amounts from selected hub plan row + cash fee setting (no GetCostBreakdownDifference). */
  useEffect(() => {
    if (activeStep !== 3) {
      setCostBreakdownLoading(false);
      return;
    }

    const planKey =
      selectedPlanId != null && selectedPlanId !== ""
        ? String(selectedPlanId)
        : "";
    if (planKey === "") {
      setCostBreakdownLoading(false);
      setCostBreakdown([]);
      setTotalPayable("₹0.00");
      setCostBreakdownRaw({});
      setOnboardingProcessingFees("0");
      setOnboardingDepositAmount("0");
      setCashPaymentCharge("");
      setAppliedCoupon(null);
      return;
    }

    const selectedPlan = plansData.find(
      (p: any) => String(p.planid ?? p.id) === planKey,
    );
    if (!selectedPlan) {
      setCostBreakdownLoading(false);
      setCostBreakdown([]);
      setTotalPayable("₹0.00");
      setCostBreakdownRaw({});
      setOnboardingProcessingFees("0");
      setOnboardingDepositAmount("0");
      setCashPaymentCharge("");
      setAppliedCoupon(null);
      return;
    }

    let cancelled = false;
    setCostBreakdownLoading(true);
    const formatRs = (n: number) =>
      `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

    const pp = Number(selectedPlan.price ?? 0);
    const pf = Number(
      selectedPlan.processing_fees ?? selectedPlan.processingFees ?? 0,
    );
    const ra = Number(
      selectedPlan.refundable_amount ?? selectedPlan.refundableAmount ?? 0,
    );

    (async () => {
      let cashFeeNum = 0;
      try {
        const settingsRes: any = await apiService.getSystemSettingsData();
        if (cancelled) return;
        const data = settingsRes?.data ?? settingsRes?.result ?? settingsRes;
        const list = Array.isArray(data) ? data : [];
        const cash = list.find(
          (s: any) => s?.systemsettingskey === "CashPaymentCharge",
        );
        if (
          cash?.systemsettingsvalue != null &&
          String(cash.systemsettingsvalue).trim() !== ""
        ) {
          cashFeeNum = Number(cash.systemsettingsvalue) || 0;
        }
      } catch {
        cashFeeNum = 0;
      }
      if (cancelled) return;

      const breakdown: CostBreakdownItem[] = [
        { label: "Plan price", value: formatRs(pp) },
        { label: "Processing fees", value: formatRs(pf) },
        { label: "Deposit amount", value: formatRs(ra) },
        { label: "Cash Handling Fees", value: formatRs(cashFeeNum) },
      ];
      const coreTotal = pp + pf + ra;
      setCostBreakdown(breakdown);
      setTotalPayable(coreTotal > 0 ? formatRs(coreTotal) : "₹0.00");
      setCostBreakdownRaw({
        planprice: pp,
        processing_fees: pf,
        refundable_amount: ra,
      });
      setOnboardingProcessingFees(String(pf));
      setOnboardingDepositAmount(String(ra));
      setCashPaymentCharge(String(cashFeeNum));
      setCostBreakdownLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [activeStep, selectedPlanId, plansData]);

  useEffect(() => {
    if (activeStep !== 3) return;
    const uid = Number(customerUserId);
    if (!Number.isFinite(uid) || uid <= 0) {
      setPromoCodeOptions([]);
      setPromoCodeMetaByValue({});
      return;
    }
    let cancelled = false;
    setPromoCodesLoading(true);
    apiService
      .getPromoCodesByUserId(uid)
      .then((res) => {
        if (cancelled) return;
        const rows = pickKycListFromResponse(res);
        const seen = new Set<string>();
        const opts: { label: string; value: string }[] = [];
        const meta: Record<
          string,
          { couponid: number; couponName: string; isReferralCoupon: boolean }
        > = {};
        rows.forEach((raw, i) => {
          if (raw == null || typeof raw !== "object") return;
          const r = raw as Record<string, unknown>;
          const name = String(r.couponname ?? r.couponName ?? "").trim();
          const idRaw = r.couponid ?? r.couponId;
          const idStr =
            idRaw != null && String(idRaw).trim() !== "" ? String(idRaw) : "";
          const value = idStr || name || `promo-${i}`;
          if (seen.has(value)) return;
          seen.add(value);
          opts.push({ label: name || value, value });
          const idNum = Number.parseInt(String(idRaw ?? ""), 10);
          const couponid = Number.isFinite(idNum) ? idNum : 0;
          const referralRaw =
            r.isreferralcoupon ?? r.isReferralCoupon ?? r.IsReferralCoupon;
          const isReferralCoupon =
            referralRaw === true ||
            referralRaw === 1 ||
            String(referralRaw).toLowerCase() === "true";
          meta[value] = {
            couponid,
            couponName: name || value,
            isReferralCoupon,
          };
        });
        setPromoCodeOptions(opts);
        setPromoCodeMetaByValue(meta);
      })
      .catch(() => {
        if (!cancelled) {
          setPromoCodeOptions([]);
          setPromoCodeMetaByValue({});
        }
      })
      .finally(() => {
        if (!cancelled) setPromoCodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep, customerUserId]);

  const costBreakdownForSelectPlan = useMemo(() => {
    const formatRs = (n: number) =>
      `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    const addStr = additionalFees.trim();
    const addNum = addStr === "" ? Number.NaN : Number.parseFloat(addStr);
    const hasAdd = Number.isFinite(addNum) && addNum !== 0;

    const discNum =
      appliedCoupon?.discountValue != null
        ? Number(appliedCoupon.discountValue)
        : Number.NaN;
    const hasDisc = Number.isFinite(discNum) && discNum > 0;
    if (!hasAdd && !hasDisc) return costBreakdown;

    // Remove any previous discount/additional rows, then re-insert at correct positions.
    let copy = costBreakdown.filter(
      (x) =>
        !/^Discount\b/i.test(String(x.label)) && x.label !== "Additional fees",
    );

    if (hasDisc && appliedCoupon) {
      const depositIdx = copy.findIndex((x) => x.label === "Deposit amount");
      const label = `Discount (${appliedCoupon.name || appliedCoupon.code})`;
      const row: CostBreakdownItem = {
        label,
        value: `-₹${Number(discNum).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
        isDiscount: true,
      };
      if (depositIdx >= 0) copy.splice(depositIdx + 1, 0, row);
      else copy.push(row);
    }

    if (hasAdd) {
      const idx = copy.findIndex((x) => x.label === "Cash Handling Fees");
      const row: CostBreakdownItem = {
        label: "Additional fees",
        value: formatRs(addNum),
      };
      if (idx >= 0) copy.splice(idx + 1, 0, row);
      else copy.push(row);
    }

    return copy;
  }, [costBreakdown, additionalFees, appliedCoupon]);

  const handleApplyCoupon = useCallback(async () => {
    if (
      selectedPlanId == null ||
      selectedPlanId === "" ||
      String(selectedPlanId).trim() === ""
    ) {
      showNotification("Select a plan first.", "error", COUPON_APPLY_NOTIFY_MS);
      return;
    }
    const code = couponCode.trim();
    if (!code) {
      showNotification(
        "Select a coupon from the list.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
      return;
    }
    const label =
      promoCodeOptions.find((o) => String(o.value) === code)?.label ?? code;
    const meta = promoCodeMetaByValue[code];
    const planPriceRaw = Number(costBreakdownRaw.planprice);
    const planprice = Number.isFinite(planPriceRaw) ? planPriceRaw : 0;
    const payload: {
      couponid: number;
      couponName: string;
      isReferralCoupon: boolean;
      planprice: number;
      userid: number;
    } = {
      couponid: meta?.couponid ?? 0,
      couponName: meta?.couponName ?? label,
      isReferralCoupon: meta?.isReferralCoupon ?? false,
      planprice,
      userid: Number(customerUserId),
    };
    if (!payload.couponName.trim()) {
      showNotification(
        "Invalid coupon selection.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
      return;
    }
    setCouponApplyLoading(true);
    try {
      const res = await apiService.checkCouponValidity(payload);
      if (couponValidityIndicatesFailure(res)) {
        const msg =
          readCouponValidityMessage(res) ?? "Coupon could not be applied.";
        showNotification(msg, "error", COUPON_APPLY_NOTIFY_MS);
        setAppliedCoupon(null);
        lastCouponValidityKeyRef.current = "";
        return;
      }
      const discountValue = readCouponDiscountValue(res);
      const couponNameFromApi = readCouponDisplayName(res);
      setAppliedCoupon({
        code,
        couponid: meta?.couponid ?? 0,
        name: couponNameFromApi || payload.couponName,
        discountValue,
        isReferralCoupon: meta?.isReferralCoupon ?? false,
      });
      lastCouponValidityKeyRef.current = `${code}|${String(selectedPlanId)}|${planprice}`;
      showNotification(
        `Coupon "${payload.couponName}" applied.`,
        "success",
        COUPON_APPLY_NOTIFY_MS,
      );
    } catch (e: unknown) {
      let msg = e instanceof Error ? e.message.trim() : "";
      if (!msg && e && typeof e === "object" && "response" in e) {
        const data = (e as { response?: { data?: unknown } }).response?.data;
        msg = readCouponValidityMessage(data) ?? "";
      }
      setAppliedCoupon(null);
      lastCouponValidityKeyRef.current = "";
      showNotification(
        msg || "Could not validate coupon.",
        "error",
        COUPON_APPLY_NOTIFY_MS,
      );
    } finally {
      setCouponApplyLoading(false);
    }
  }, [
    couponCode,
    costBreakdownRaw.planprice,
    customerUserId,
    promoCodeMetaByValue,
    promoCodeOptions,
    selectedPlanId,
    showNotification,
  ]);

  useEffect(() => {
    if (!appliedCoupon) return;
    const current = couponCode.trim();
    if (current !== appliedCoupon.code) {
      setAppliedCoupon(null);
    }
  }, [couponCode, appliedCoupon]);

  useEffect(() => {
    if (activeStep !== 3) return;
    if (!appliedCoupon) {
      lastCouponValidityKeyRef.current = "";
      return;
    }
    if (
      selectedPlanId == null ||
      selectedPlanId === "" ||
      String(selectedPlanId).trim() === ""
    )
      return;
    if (costBreakdownLoading) return;
    const planprice = Number(costBreakdownRaw.planprice);
    if (!Number.isFinite(planprice)) return;

    const code = appliedCoupon.code;
    const meta = promoCodeMetaByValue[code];
    const key = `${code}|${String(selectedPlanId)}|${planprice}`;
    if (key === lastCouponValidityKeyRef.current) return;

    const userid = Number(customerUserId);
    if (!Number.isFinite(userid) || userid <= 0) return;

    const couponName =
      appliedCoupon.name.trim() !== ""
        ? appliedCoupon.name
        : (meta?.couponName ?? code);
    const payload = {
      couponid: meta?.couponid ?? appliedCoupon.couponid ?? 0,
      couponName,
      isReferralCoupon: appliedCoupon.isReferralCoupon,
      planprice,
      userid,
    };
    if (!payload.couponName.trim()) return;

    const flightId = ++couponPlanRevalidateFlightRef.current;
    let cancelled = false;
    setCouponApplyLoading(true);
    (async () => {
      try {
        const res = await apiService.checkCouponValidity(payload);
        if (cancelled || flightId !== couponPlanRevalidateFlightRef.current)
          return;
        if (couponValidityIndicatesFailure(res)) {
          const msg =
            readCouponValidityMessage(res) ??
            "Coupon could not be applied for this plan.";
          showNotification(msg, "error", COUPON_APPLY_NOTIFY_MS);
          setAppliedCoupon(null);
          lastCouponValidityKeyRef.current = "";
          return;
        }
        const discountValue = readCouponDiscountValue(res);
        const couponNameFromApi = readCouponDisplayName(res);
        lastCouponValidityKeyRef.current = key;
        setAppliedCoupon({
          code,
          couponid: meta?.couponid ?? appliedCoupon.couponid ?? 0,
          name: couponNameFromApi || payload.couponName,
          discountValue,
          isReferralCoupon: appliedCoupon.isReferralCoupon,
        });
      } catch (e: unknown) {
        if (cancelled || flightId !== couponPlanRevalidateFlightRef.current)
          return;
        let msg = e instanceof Error ? e.message.trim() : "";
        if (!msg && e && typeof e === "object" && "response" in e) {
          const data = (e as { response?: { data?: unknown } }).response?.data;
          msg = readCouponValidityMessage(data) ?? "";
        }
        setAppliedCoupon(null);
        lastCouponValidityKeyRef.current = "";
        showNotification(
          msg || "Could not validate coupon.",
          "error",
          COUPON_APPLY_NOTIFY_MS,
        );
      } finally {
        if (!cancelled && flightId === couponPlanRevalidateFlightRef.current) {
          setCouponApplyLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeStep,
    appliedCoupon,
    costBreakdownLoading,
    costBreakdownRaw.planprice,
    customerUserId,
    promoCodeMetaByValue,
    selectedPlanId,
    showNotification,
  ]);

  const getEditBookingPaymentMethodCode = (): number | undefined => {
    const row = paymentmentCodes.find(
      (c) =>
        String(c.codedisplayname ?? "")
          .trim()
          .toLowerCase() === "cash",
    );
    if (!row) return undefined;
    const n = Number.parseInt(String(row.codevalue));
    return Number.isFinite(n) ? n : undefined;
  };

  /**
   * Resolves numeric `paymentmethod` for online/Razorpay from Code Common "PaymentMethod".
   * Master data rarely uses the exact string "online" — often "Online Payment", "Razorpay", "UPI", etc.
   */
  const getOnlinePaymentMethodCode = (): number | undefined => {
    const list = paymentmentCodes;
    if (!list?.length) return undefined;

    const norm = (s: unknown) =>
      String(s ?? "")
        .trim()
        .toLowerCase();
    const codeFromRow = (r: { codevalue?: string | number }) => {
      const n = Number.parseInt(String(r.codevalue), 10);
      return Number.isFinite(n) ? n : undefined;
    };

    let row = list.find((c) => norm(c.codedisplayname) === "online");
    if (row) return codeFromRow(row);

    row = list.find((c) => norm(c.codedisplayname).includes("online"));
    if (row) return codeFromRow(row);

    const keywords = [
      "razorpay",
      "upi",
      "card",
      "netbanking",
      "digital",
      "gateway",
      "pg",
      "wallet",
    ];
    for (const kw of keywords) {
      row = list.find((c) => norm(c.codedisplayname).includes(kw));
      if (row) {
        const code = codeFromRow(row);
        if (code != null) return code;
      }
    }

    const cashIdx = list.findIndex((c) => norm(c.codedisplayname) === "cash");
    if (list.length >= 2 && cashIdx >= 0) {
      const other = list.find((_, i) => i !== cashIdx);
      if (other) {
        const code = codeFromRow(other);
        if (code != null) return code;
      }
    }

    return undefined;
  };

  /** POST /Booking_Trip_ManagementService/Booking/CreateBooking */
  const buildCreateBookingPayload = (opts?: {
    /** Razorpay order id when booking is created after CreateRazorpayOrder (online flow). */
    razorpayOrderId?: string;
  }): Record<string, unknown> => {
    const b = bookingDetails || {};
    const toNum = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    const toStr = (v: unknown): string => (v == null ? "" : String(v));

    const hubId = toNum(selectedHubId);
    const vehicleId = toNum(selectedVehicleId);
    const vehicleModelId = toNum(selectedModelId);
    const planIdNum = toNum(selectedPlanId);

    const selectedVehicle = hubVehicles.find(
      (v: any) => String(v.vehicleid ?? v.id) === String(selectedVehicleId),
    );
    const selectedPlan = plansData.find(
      (p: any) => String(p.planid ?? p.id) === String(selectedPlanId),
    );
    const selectedHub = hubs.find(
      (h: any) => Number(h.hubmasterid) === Number(selectedHubId),
    );

    const planStartDate = newPlanWindowForCreate?.planStartISO ?? "";
    const planEndDate = newPlanWindowForCreate?.planEndISO ?? "";

    const corePayable = totalPayable
      ? Number.parseFloat(String(totalPayable).replaceAll(/[₹,\s]/g, "")) || 0
      : 0;
    const addStr = additionalFees.trim();
    const addParsed = addStr === "" ? Number.NaN : Number.parseFloat(addStr);
    const additionalFeesNum = Number.isFinite(addParsed) ? addParsed : 0;
    const discountNum =
      appliedCoupon?.discountValue != null
        ? Number(appliedCoupon.discountValue)
        : Number.NaN;
    const discountForMath =
      Number.isFinite(discountNum) && discountNum > 0 ? discountNum : 0;
    const discountedAmountPayload: number =
      Number.isFinite(discountNum) && discountNum > 0 ? discountNum : 0;
    const bookingAmountWithFees = Math.max(
      0,
      corePayable + additionalFeesNum - discountForMath,
    );

    const cashCharge =
      paymentMethod === "cash" && cashPaymentCharge !== ""
        ? Number(cashPaymentCharge) || 0
        : 0;

    const createdBy = toNum(customerUserId);
    const modifiedBy = toNum(user?.userid);
    const managerId = toNum(user?.userid);

    const pp = toNum(costBreakdownRaw.planprice);
    const pf = toNum(costBreakdownRaw.processing_fees);
    const ra = toNum(costBreakdownRaw.refundable_amount);

    const couponid =
      appliedCoupon?.couponid != null ? toNum(appliedCoupon.couponid) : 0;
    const couponname = appliedCoupon?.name?.trim?.()
      ? String(appliedCoupon.name).trim()
      : "";
    const isReferralCoupon = appliedCoupon?.isReferralCoupon === true;

    const planGstDetails = buildPlanGstDetailsPayload(selectedPlan, planIdNum, {
      planprice: pp,
      processingFees: pf,
      refundableAmount: ra,
      additionalFees: additionalFeesNum,
      cashCharge,
    });

    return {
      hubid: hubId,
      vehicleid: vehicleId,
      evtypeid: toNum(selectedVehicle?.speedtype ?? selectedVehicle?.speedType),
      vehicletypeid: toNum(
        selectedVehicle?.vehicletype ?? selectedVehicle?.vehicleType,
      ),
      createdby: createdBy,
      modifiedby: modifiedBy,
      pickupstarttime: planStartDate,
      pickupendtime: planEndDate,
      name: toStr(customerName),
      planname: toStr(
        selectedPlan?.planname ?? selectedPlan?.planName ?? b.planname,
      ),
      vehiclename: toStr(
        selectedVehicle?.vehiclename ??
          selectedVehicle?.vehicleName ??
          b.vehiclename,
      ),
      phonenumber: toStr(phoneNumber ?? b.phonenumber),
      hubname: toStr(selectedHub?.hubname ?? selectedHub?.hubName ?? b.hubname),
      telematicsdeviceid: toStr(
        selectedVehicle?.telematicsdeviceid ??
          selectedVehicle?.telematicsDeviceId ??
          b.telematicsdeviceid,
      ),
      vehiclemodelid: vehicleModelId,
      vehiclenum: toStr(
        selectedVehicle?.vehiclenum ??
          selectedVehicle?.vehicleNum ??
          b.vehiclenum,
      ),
      registrationnumber: toStr(
        selectedVehicle?.registrationnumber ??
          selectedVehicle?.registrationNumber ??
          b.registrationnumber,
      ),
      bookingPlanModel: {
        bookingmasterid: 0,
        planid: planIdNum,
        ratetemplatemasterid: toNum(
          selectedPlan?.templatemasterid ??
            selectedPlan?.ratetemplatemasterid ??
            selectedPlan?.rateTemplateMasterId,
        ),
        planstartdate: planStartDate,
        planenddate: planEndDate,
        bookingamount: bookingAmountWithFees,
        extrakmcharge: toNum(
          selectedPlan?.extrakmcharge ?? selectedPlan?.extraKmCharge,
        ),
        latereturnfees: toNum(
          selectedPlan?.latereturnfees ?? selectedPlan?.lateReturnFees,
        ),
        extrakmchargeperkm: toNum(
          selectedPlan?.extrakmchargeperkm ?? selectedPlan?.extraKmChargePerKm,
        ),
        plankmlimit: toNum(
          selectedPlan?.plankmlimit ??
            selectedPlan?.planKmLimit ??
            selectedPlan?.allowedkm ??
            selectedPlan?.allowedKm,
        ),
        bookingplanid: 0,
        cashpaymentchargeamount: cashCharge,
        price: pp,
        processing_fees: pf,
        refundable_amount: ra,
        amount_adjustment_reason: toStr(
          [
            remarks,
            onboardingFeeRemarks ? `Onboarding: ${onboardingFeeRemarks}` : "",
          ]
            .map((x) => String(x).trim())
            .filter((x) => x !== "")
            .join(" | "),
        ),
        couponid,
        couponname,
        discountedamount: discountedAmountPayload,
        isReferralCoupon,
        planGstDetails,
      },
      feesModificationModel: {
        fee_modification_id: 0,
        bookingmasterid: 0,
        planid: planIdNum,
        additional_amount: additionalFeesNum,
        managerid: managerId,
      },
      isStartOdometerPending: true,
      managerid: managerId,
      orderid: `${opts?.razorpayOrderId ?? ""}`.trim(),
      isFromRazorpay: false,
      ispaymentpending: true,
    };
  };

  const handleSaveClick = () => {
    if (!vehicleStepValid) {
      setShowVehicleUploadValidation(true);
      setError(
        kycSelectionInvalid
          ? (kycVehicleStepBlockMessage ?? KYC_INCOMPLETE_VEHICLE_MESSAGE)
          : "Please complete the vehicle step before saving.",
      );
      return;
    }
    setSaveConfirmOpen(true);
  };

  const executeSave = async () => {
    if (!vehicleStepValid) {
      setShowVehicleUploadValidation(true);
      setError(
        kycSelectionInvalid
          ? (kycVehicleStepBlockMessage ?? KYC_INCOMPLETE_VEHICLE_MESSAGE)
          : "Please complete the vehicle step before saving.",
      );
      setSaveLoading(false);
      return;
    }
    setError(null);
    try {
      const payerNameForPayment =
        customerName !== "—" ? customerName.trim() : "";
      const payerMobileForPayment =
        phoneNumber !== "—" ? phoneNumber.trim() : "";

      const amountForPayment =
        Number.parseFloat(
          String(displayTotalPayable).replaceAll(/[₹,\s]/g, ""),
        ) || 0;
      const discountedamountForSave =
        appliedCoupon?.discountValue != null &&
        Number.isFinite(Number(appliedCoupon.discountValue)) &&
        Number(appliedCoupon.discountValue) > 0
          ? Number(appliedCoupon.discountValue)
          : 0;

      const selectedPlanRow = plansData.find(
        (p: any) => String(p.planid ?? p.id) === String(selectedPlanId),
      );
      const planLabel =
        `${selectedPlanRow?.planname ?? planOptions.find((p) => String(p.id) === String(selectedPlanId))?.name ?? ""}`.trim();
      const modelLabel =
        `${selectedVehicleModelDisplayName ?? ""}`.trim() || "Vehicle model";
      const summeryofpayments = `Booking payment for ${modelLabel || "-"} - ${planLabel || "-"}`;

      /** Online: Razorpay order → dialog → CreatePaymentLink/QR → CreateBooking → SavePaymentDetails */
      if (amountForPayment > 0 && paymentMethod === "online") {
        const orderReceipt = `${Date.now()}`;
        const amountPaise = Math.round(amountForPayment * 100);

        let orderResForDetails: unknown;
        let orderId: string;
        try {
          orderResForDetails = await apiService.createRazorpayOrder({
            amount: amountPaise,
            currency: "INR",
            receipt: orderReceipt,
            notes: {
              customer_name: payerNameForPayment.trim() || "",
              phone_number: String(payerMobileForPayment).replace(/\D/g, ""),
            },
            extraobject: [{ type: "booking" }],
          });
          const extracted = extractRazorpayOrderId(orderResForDetails);
          if (!extracted) {
            throw new Error(
              "Failed to create Razorpay order. Order ID not received.",
            );
          }
          orderId = extracted;
        } catch (orderErr: any) {
          const msg =
            orderErr?.message ||
            orderErr?.response?.data?.message ||
            "Failed to create payment order.";
          setError(msg);
          setSaveLoading(false);
          return;
        }

        const onlinePaymentMethodCode = getOnlinePaymentMethodCode();
        if (onlinePaymentMethodCode == null) {
          setError(
            'Online payment method could not be resolved from master data. Ensure Code Common category "PaymentMethod" includes a non-cash method (e.g. Online, Online Payment, Razorpay, or UPI) with a valid code value.',
          );
          setSaveLoading(false);
          return;
        }

        const customerNameForPayment =
          payerNameForPayment ||
          (customerName !== "—" ? customerName.trim() : "") ||
          "Customer";
        const customerContact = String(
          payerMobileForPayment ?? phoneNumber ?? "",
        ).replace(/\D/g, "");
        const customerEmail = "noreply@motovolt.in";
        const referenceId = `${orderId}`;

        const paymentLinkBody = {
          amount: amountPaise,
          currency: "INR",
          accept_partial: false,
          description: summeryofpayments,
          reference_id: referenceId,
          customer: {
            name: customerNameForPayment,
            email: customerEmail,
            contact: customerContact,
          },
          notify: { sms: true, email: true },
          reminder_enable: true,
          notes: {
            env: process.env.REACT_APP_ENV || "UAT",
            module: "Booking",
            razorpayOrderId: orderId,
            orderReceipt,
            customerUserId: String(customerUserId),
          },
        };

        const qrCodeBody = {
          type: "upi_qr",
          name: customerNameForPayment,
          usage: "single_use",
          fixed_amount: true,
          payment_amount: amountPaise,
          description: summeryofpayments,
          orderid: orderId,
          notes: {
            customerUserId: String(customerUserId),
          },
        };

        setOnlineCollectionPaymentLink("");
        setOnlineCollectionQrSrc("");
        setOnlineCollectionVerifyEntityId(null);
        setOnlineCollectionOrderId(orderId);
        setOnlineCollectionError(null);
        setOnlineCollectionBookingDone(false);
        setOnlineCollectionDialogOpen(true);
        setOnlineCollectionLoading(true);
        setSaveLoading(false);

        try {
          let collectionRes: unknown;
          const onlinePayerMobile =
            `${payerMobileForPayment ?? ""}`.trim() ||
            `${phoneNumber !== "—" ? phoneNumber : ""}`.trim() ||
            customerContact;

          if (postSavePaymentMode === "payment_link") {
            collectionRes = await apiService.createPaymentLink(paymentLinkBody);
            const link =
              extractPaymentLinkFromCollectionResponse(collectionRes);
            if (!link) {
              throw new Error("Payment link was not returned by the server.");
            }

            const createRes = await apiService.createBooking(
              buildCreateBookingPayload({ razorpayOrderId: orderId }),
            );
            const createWrap = createRes as {
              isSuccess?: boolean;
              message?: string;
            };
            if (
              createWrap &&
              typeof createWrap === "object" &&
              createWrap.isSuccess === false
            ) {
              throw new Error(
                typeof createWrap.message === "string" &&
                  createWrap.message.trim() !== ""
                  ? createWrap.message
                  : "Create booking failed.",
              );
            }
            const newBookingId = extractCreatedBookingId(createRes);
            if (!newBookingId) {
              throw new Error(
                "Booking was created but no booking ID was returned.",
              );
            }
            setOnlineCollectionBookingDone(true);
            const successMsg = extractCreateBookingSuccessMessage(createRes);
            if (successMsg) showNotification(successMsg, "success");

            const entityIdFromApi =
              extractPaymentIdFromCollectionResponse(collectionRes, "") || null;
            setOnlineCollectionVerifyEntityId(entityIdFromApi);
            const transactionRef =
              entityIdFromApi ??
              extractPaymentIdFromCollectionResponse(collectionRes, orderId);
            try {
              await apiService.savePaymentDetails({
                paymentid: 0,
                hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
                bookingid: newBookingId,
                bookingplanid: resolveBookingPlanIdForPayment(createRes),
                username: customerNameForPayment,
                mobileno: onlinePayerMobile,
                hubname: `${selectedHubDisplayName ?? ""}`.trim(),
                amount: amountForPayment,
                paymentmethod: onlinePaymentMethodCode,
                summeryofpayments,
                transactionid: transactionRef,
                enteredon: dayjs().toISOString(),
                discountedamount: discountedamountForSave,
                orderid: orderId,
              });
            } catch (dErr: unknown) {
              console.warn("SavePaymentDetails failed", dErr);
            }

            setOnlineCollectionPaymentLink(link);
          } else {
            collectionRes = await apiService.createQrCode(qrCodeBody);
            const qr = extractQrImageSrcFromCollectionResponse(collectionRes);
            if (!qr) {
              throw new Error("QR image was not returned by the server.");
            }

            const createRes = await apiService.createBooking(
              buildCreateBookingPayload({ razorpayOrderId: orderId }),
            );
            const createWrap = createRes as {
              isSuccess?: boolean;
              message?: string;
            };
            if (
              createWrap &&
              typeof createWrap === "object" &&
              createWrap.isSuccess === false
            ) {
              throw new Error(
                typeof createWrap.message === "string" &&
                  createWrap.message.trim() !== ""
                  ? createWrap.message
                  : "Create booking failed.",
              );
            }
            const newBookingId = extractCreatedBookingId(createRes);
            if (!newBookingId) {
              throw new Error(
                "Booking was created but no booking ID was returned.",
              );
            }
            setOnlineCollectionBookingDone(true);
            const successMsg = extractCreateBookingSuccessMessage(createRes);
            if (successMsg) showNotification(successMsg, "success");

            const entityIdFromApi =
              extractPaymentIdFromCollectionResponse(collectionRes, "") || null;
            setOnlineCollectionVerifyEntityId(entityIdFromApi);
            const transactionRef =
              entityIdFromApi ??
              extractPaymentIdFromCollectionResponse(collectionRes, orderId);
            try {
              await apiService.savePaymentDetails({
                paymentid: 0,
                hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
                bookingid: newBookingId,
                bookingplanid: resolveBookingPlanIdForPayment(createRes),
                username: customerNameForPayment,
                mobileno: onlinePayerMobile,
                hubname: `${selectedHubDisplayName ?? ""}`.trim(),
                amount: amountForPayment,
                paymentmethod: onlinePaymentMethodCode,
                summeryofpayments,
                transactionid: transactionRef,
                enteredon: dayjs().toISOString(),
                discountedamount: discountedamountForSave,
                orderid: orderId,
              });
            } catch (dErr: unknown) {
              console.warn("SavePaymentDetails failed", dErr);
            }

            setOnlineCollectionQrSrc(qr);
          }
        } catch (err: any) {
          const msg =
            err?.message ||
            err?.response?.data?.message ||
            "Failed to create booking or payment collection.";
          setOnlineCollectionError(msg);
        } finally {
          setOnlineCollectionLoading(false);
        }
        return;
      }

      const payload = buildCreateBookingPayload();
      const createRes = await apiService.createBooking(payload);
      const createWrap = createRes as { isSuccess?: boolean; message?: string };
      if (
        createWrap &&
        typeof createWrap === "object" &&
        createWrap.isSuccess === false
      ) {
        throw new Error(
          typeof createWrap.message === "string" &&
            createWrap.message.trim() !== ""
            ? createWrap.message
            : "Create booking failed.",
        );
      }
      const newBookingId = extractCreatedBookingId(createRes);

      if (amountForPayment > 0 && paymentMethod === "cash") {
        const cashPaymentMethodCode = getEditBookingPaymentMethodCode();
        if (cashPaymentMethodCode == null) {
          showNotification(
            "Cash payment method could not be resolved from master data. Ensure PaymentMethod codes are loaded and Cash is configured.",
            "error",
          );
          setSaveLoading(false);
          navigateToBookingManagementAfterSuccess();
          return;
        }
        if (!newBookingId) {
          showNotification(
            "Booking was created but no booking ID was returned. Verify payment in booking list.",
            "error",
          );
          setSaveLoading(false);
          navigateToBookingManagementAfterSuccess();
          return;
        }

        const paymentBody = {
          paymentid: 0,
          hubmasterid: selectedHubId != null ? Number(selectedHubId) : 0,
          bookingid: newBookingId,
          username: payerNameForPayment || "Customer",
          mobileno: payerMobileForPayment,
          hubname: `${selectedHubDisplayName ?? ""}`.trim(),
          amount: amountForPayment,
          paymentmethod: cashPaymentMethodCode,
          summeryofpayments,
          transactionid: "cash",
          enteredon: dayjs().toISOString(),
          discountedamount: discountedamountForSave,
          bookingplanid: resolveBookingPlanIdForPayment(createRes),
          orderid: "",
        };

        await apiService.savePaymentDetails(paymentBody);
      }

      const successMsg = extractCreateBookingSuccessMessage(createRes);
      if (successMsg) showNotification(successMsg, "success");
      navigateToBookingManagementAfterSuccess();
    } catch (e: any) {
      const msg =
        e?.message ||
        e?.response?.data?.message ||
        "Failed to create booking or save payment.";
      setError(msg);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleConfirmSave = () => {
    setSaveConfirmOpen(false);
    setSaveLoading(true);
    void executeSave();
  };

  const handleVerifyQrPayment = useCallback(async () => {
    const oid = onlineCollectionOrderId.trim();
    if (!oid) {
      showNotification("Order ID is missing.", "error");
      return;
    }

    verifyPaymentPollAbortRef.current = false;
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, ms);
      });

    setVerifyPaymentLoading(true);
    let createdNotified = false;
    const maxAttempts = 36;

    const finishSuccess = () => {
      const done = onlineCollectionBookingDoneRef.current;
      setOnlineCollectionDialogOpen(false);
      setOnlineCollectionError(null);
      setOnlineCollectionPaymentLink("");
      setOnlineCollectionQrSrc("");
      setOnlineCollectionOrderId("");
      setOnlineCollectionVerifyEntityId(null);
      setOnlineCollectionBookingDone(false);
      if (done) navigateToBookingManagementAfterSuccess();
    };

    try {
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        if (verifyPaymentPollAbortRef.current) {
          return;
        }

        const raw = (await apiService.verifyPaymentByOrderId(
          oid,
          postSavePaymentMode === "payment_link" ? "payment_link" : "QR",
          onlineCollectionVerifyEntityId,
        )) as Record<string, unknown>;
        const status = String(raw.status ?? raw.Status ?? "")
          .trim()
          .toLowerCase();

        if (status === "failed") {
          showNotification(
            pickVerifyPaymentResponseMessage(raw) ??
              "Payment verification failed.",
            "error",
          );
          return;
        }

        if (status === "captured") {
          showNotification(
            pickVerifyPaymentResponseMessage(raw) ??
              "Payment verified successfully.",
            "success",
          );
          finishSuccess();
          return;
        }

        if (status === "created") {
          if (!createdNotified) {
            showNotification(
              "Payment is under process. Checking again in a few seconds.",
              "info",
            );
            createdNotified = true;
          }
          if (verifyPaymentPollAbortRef.current) {
            return;
          }
          await sleep(10000);
          continue;
        }

        if (raw.isSuccess === false || raw.success === false) {
          showNotification(
            pickVerifyPaymentResponseMessage(raw) ?? "Verification failed.",
            "error",
          );
          return;
        }
        if (raw.isSuccess === true || raw.success === true) {
          showNotification(
            pickVerifyPaymentResponseMessage(raw) ??
              "Payment verified successfully.",
            "success",
          );
          finishSuccess();
          return;
        }

        showNotification(
          pickVerifyPaymentResponseMessage(raw) ??
            "Unexpected verification response.",
          "error",
        );
        return;
      }

      showNotification(
        "Payment is still pending. Please try verifying again later.",
        "error",
      );
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message.trim() !== ""
          ? e.message
          : "Verification request failed.";
      showNotification(msg, "error");
    } finally {
      setVerifyPaymentLoading(false);
    }
  }, [
    onlineCollectionOrderId,
    onlineCollectionVerifyEntityId,
    postSavePaymentMode,
    showNotification,
    navigateToBookingManagementAfterSuccess,
  ]);

  return (
    <Container
      fluid
      className="px-0"
      style={{ maxWidth: "100%", overflowX: "hidden" }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          px: { xs: 1.5, sm: 3, md: 4 },
          pb: { xs: 3, sm: 4 },
        }}
      >
        {saveLoading && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 2,
              bgcolor: "rgba(255, 255, 255, 0.14)",
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomLoading message="Creating booking..." />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e0e0e0",
            bgcolor: "#fff",
            borderRadius: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              borderBottom: "2px solid",
              borderColor: THEME_PRIMARY,
              pb: 1,
              mb: 2,
              color: THEME_PRIMARY,
              fontSize: { xs: 16, sm: 18 },
            }}
          >
            Customer Information
          </Typography>
          <Row className="g-3">
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Customer user ID
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {customerUserId}
              </Typography>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Customer Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {customerName}
              </Typography>
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ mb: 0.25 }}
              >
                Phone Number
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {phoneNumber}
              </Typography>
            </Col>
          </Row>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            width: "100%",
            p: { xs: 2, sm: 3 },
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e0e0e0",
            bgcolor: "#fff",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2.5, sm: 4, md: 5, lg: 10 },
              justifyContent: "flex-start",
              alignItems: "flex-end",
              mb: { xs: 3, sm: 4 },
            }}
          >
            {steps.map((label, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              let textColor = "grey.500";
              let underlineColor = "grey.400";
              if (isCompleted) {
                textColor = "success.main";
                underlineColor = "success.main";
              } else if (isActive) {
                textColor = THEME_PRIMARY;
                underlineColor = THEME_PRIMARY;
              }
              return (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    flex: { xs: "1 1 40%", sm: "0 0 auto" },
                    minWidth: 0,
                    borderBottom: "2px solid",
                    borderColor: underlineColor,
                    pb: 1.25,
                    "&:first-of-type": { ml: 0 },
                    "&:last-of-type": { mr: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 0.75 },
                      flexWrap: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {isCompleted ? (
                      <CheckCircleIcon
                        sx={{
                          color: "success.main",
                          fontSize: { xs: 20, sm: 24, md: 26 },
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: textColor,
                        fontSize: {
                          xs: "0.9375rem",
                          sm: "1rem",
                          md: "1.0625rem",
                        },
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {isCompleted ? label : `${index + 1}. ${label}`}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {activeStep === 0 && (
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: { xs: 1.5, sm: 2 },
                  color: "text.primary",
                  fontSize: { xs: 18, sm: 20 },
                }}
              >
                Please Select Hub
              </Typography>
              {customerKycState.checked &&
                !customerKycState.hasKycRecord &&
                customerKycState.missingMessage != null && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {customerKycState.missingMessage}
                  </Alert>
                )}
              {hubsLoading ? (
                <CustomLoading message="Loading hubs..." />
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      lg: "repeat(3, 1fr)",
                    },
                    gap: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                    alignItems: "stretch",
                  }}
                >
                  {hubCards.map((hub) => {
                    const isSelected = selectedHubId === hub.hubmasterid;
                    return (
                      <Card
                        key={hub.hubmasterid}
                        onClick={() => handleHubSelect(hub.hubmasterid)}
                        sx={{
                          cursor: "pointer",
                          border: "1px solid",
                          borderColor: isSelected
                            ? THEME_PRIMARY_LIGHT
                            : "rgba(22, 67, 135, 0.25)",
                          borderRadius: "12px",
                          bgcolor: isSelected ? "#E8E8FF" : "#fff",
                          boxShadow: "none",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          minHeight: { xs: 180, sm: 220 },
                          "&:hover": {
                            borderColor: THEME_PRIMARY_LIGHT,
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 0,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            "&:last-child": { pb: 0 },
                          }}
                        >
                          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                            <Box
                              sx={{
                                flex: 1,
                                minWidth: 0,
                                px: { xs: 1.5, sm: 2 },
                                pt: { xs: 1.5, sm: 2 },
                                pb: 1,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 700,
                                  color: THEME_PRIMARY,
                                  fontSize: { xs: "1rem", sm: "1.1rem" },
                                  mb: 2,
                                  lineHeight: 1.4,
                                }}
                              >
                                {hub.hubname}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                  mb: 0.5,
                                }}
                              >
                                <LocationOnIcon
                                  sx={{
                                    fontSize: 20,
                                    color: THEME_PRIMARY,
                                    mt: 0.25,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666666",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                    lineHeight: 1.5,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {hub.address}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <DirectionsWalkIcon
                                  sx={{
                                    fontSize: 20,
                                    color: THEME_PRIMARY,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "#666666",
                                    fontSize: {
                                      xs: "0.8125rem",
                                      sm: "0.875rem",
                                    },
                                  }}
                                >
                                  {hub.operationhours}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                width: { xs: 88, sm: 110, md: 120 },
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                p: 1.5,
                                alignSelf: "stretch",
                              }}
                            >
                              <Box
                                component="img"
                                src={hub.imageUrl || "/scooter-placeholder.png"}
                                alt={hub.hubname}
                                onError={(
                                  e: React.SyntheticEvent<HTMLImageElement>,
                                ) => {
                                  e.currentTarget.src =
                                    "/scooter-placeholder.png";
                                }}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  minHeight: 80,
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  maxWidth: { xs: 72, sm: 88, md: 104 },
                                  maxHeight: 120,
                                }}
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              borderTop: "1px solid",
                              borderTopColor: isSelected
                                ? THEME_PRIMARY
                                : "#DDDDDD",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                              px: { xs: 1.5, sm: 2 },
                              py: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                flexWrap: "wrap",
                                minWidth: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#666666",
                                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                }}
                              >
                                {hub.available}
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  color: "#666666",
                                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                }}
                              >
                                {" "}
                              </Typography>
                            </Box>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNavigate(hub);
                              }}
                              startIcon={
                                <NavigationIcon
                                  sx={{ fontSize: 20, color: THEME_PRIMARY }}
                                />
                              }
                              sx={{
                                bgcolor: "#FFC107",
                                color: THEME_PRIMARY,
                                fontWeight: 500,
                                fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                                minWidth: 108,
                                flexShrink: 0,
                                borderRadius: "8px",
                                px: 1.5,
                                py: 0.75,
                                "&:hover": { bgcolor: "#FFB300" },
                              }}
                            >
                              Navigate
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
            </>
          )}

          {activeStep === 1 && (
            <SelectVehicleModelStep
              options={vehicleModelOptions}
              selectedId={selectedModelId}
              onSelect={setSelectedModelId}
              loading={vehiclesLoading}
              selectedHubName={selectedHubDisplayName}
              kycBlocksHighSpeedModels={
                customerKycState.checked &&
                (!customerKycState.verified ||
                  !customerKycState.hasDlOcrFullname)
              }
              kycCheckLoading={customerKycState.loading}
              kycHighSpeedMessage={KYC_HIGH_SPEED_DL_MESSAGE}
              currentBookingVehicle={null}
            />
          )}

          {activeStep === 2 && (
            <SelectVehicleStep
              vehicles={vehicleOptions}
              selectedId={selectedVehicleId}
              onSelect={handleVehicleStepSelect}
              loading={vehiclesLoading}
              vehicleListSearch={
                selectedHubId != null &&
                selectedModelId != null &&
                selectedModelId !== ""
                  ? {
                      hubMasterId: Number(selectedHubId),
                      vehicleModelId: selectedModelId,
                      getSpeedTypeDisplayName,
                    }
                  : undefined
              }
              selectedHubName={selectedHubDisplayName}
              selectedVehicleModelName={selectedVehicleModelDisplayName}
              currentBookingVehicle={null}
              showVehicleUploadValidation={showVehicleUploadValidation}
              vehiclePhotos={vehiclePhotos}
              batteryPhotos={batteryPhotos}
              chargerPhotos={chargerPhotos}
              onVehiclePhotosChange={(added) =>
                setVehiclePhotos((prev) => [...prev, ...added])
              }
              onBatteryPhotosChange={(added) =>
                setBatteryPhotos((prev) => [...prev, ...added])
              }
              onChargerPhotosChange={(added) =>
                setChargerPhotos((prev) => [...prev, ...added])
              }
              onVehiclePhotoDelete={(index) =>
                setVehiclePhotos((prev) => prev.filter((_, i) => i !== index))
              }
              onBatteryPhotoDelete={(index) =>
                setBatteryPhotos((prev) => prev.filter((_, i) => i !== index))
              }
              onChargerPhotoDelete={(index) =>
                setChargerPhotos((prev) => prev.filter((_, i) => i !== index))
              }
              checklistMappings={vehicleConditionChecklistMappings}
              onChecklistChange={setVehicleConditionChecklistMappings}
              tripStatus={tripStatusFromApi}
              tripStart={bookingDetails?.tripStart ?? null}
              originalVehicleId={
                bookingDetails?.vehicleid != null
                  ? Number(bookingDetails.vehicleid)
                  : null
              }
              isEditingOldChecklist={isEditingOldChecklist}
              onEditingOldChecklistChange={setIsEditingOldChecklist}
              newVehiclePhotos={newVehiclePhotos}
              newVehicleBatteryPhotos={newVehicleBatteryPhotos}
              newVehicleChargerPhotos={newVehicleChargerPhotos}
              newVehicleChecklistMappings={newVehicleChecklistMappings}
              onNewVehiclePhotosChange={(added) =>
                setNewVehiclePhotos((prev) => [...prev, ...added].slice(0, 5))
              }
              onNewVehicleBatteryPhotosChange={(added) =>
                setNewVehicleBatteryPhotos((prev) =>
                  [...prev, ...added].slice(0, 3),
                )
              }
              onNewVehicleChargerPhotosChange={(added) =>
                setNewVehicleChargerPhotos((prev) =>
                  [...prev, ...added].slice(0, 3),
                )
              }
              onNewVehiclePhotoDelete={(index) =>
                setNewVehiclePhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleBatteryPhotoDelete={(index) =>
                setNewVehicleBatteryPhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleChargerPhotoDelete={(index) =>
                setNewVehicleChargerPhotos((prev) =>
                  prev.filter((_, i) => i !== index),
                )
              }
              onNewVehicleChecklistChange={setNewVehicleChecklistMappings}
              originalVehicleType={originalVehicleType}
              selectedVehicleType={selectedVehicleType}
              kycBlocksOnePersonVehicles={kycBlocksOnePersonVehicles}
              kycBlocksHighSpeedVehicles={kycBlocksHighSpeedVehicles}
              kycCheckLoading={customerKycState.loading}
              kycIncompleteMessage={KYC_INCOMPLETE_VEHICLE_MESSAGE}
              kycHighSpeedMessage={KYC_HIGH_SPEED_DL_MESSAGE}
              onRefreshVehicles={
                selectedHubId != null
                  ? () => fetchVehiclesForHub(selectedHubId)
                  : undefined
              }
            />
          )}

          {activeStep === 3 && (
            <SelectPlanStep
              plans={planOptions}
              selectedPlanId={selectedPlanId}
              onSelectPlan={setSelectedPlanId}
              couponCode={couponCode}
              onCouponChange={setCouponCode}
              onApplyCoupon={handleApplyCoupon}
              additionalFees={additionalFees}
              onAdditionalFeesChange={setAdditionalFees}
              paymentMethod={paymentMethod}
              onPaymentMethodChange={setPaymentMethod}
              costBreakdown={costBreakdownForSelectPlan}
              totalPayable={displayTotalPayable}
              loading={plansLoading}
              currentBookingContext={null}
              selectedHubName={selectedHubDisplayName}
              selectedModelName={selectedVehicleModelDisplayName}
              yourSelectionVehicleDetails={yourSelectionVehicleDetails}
              costBreakdownLoading={costBreakdownLoading}
              onboardingProcessingFees={onboardingProcessingFees}
              onboardingDepositAmount={onboardingDepositAmount}
              onboardingRemarks={onboardingFeeRemarks}
              onOnboardingFeeChange={(
                processingFees,
                depositAmount,
                remarksValue,
              ) => {
                const pf = Number.parseFloat(String(processingFees).trim());
                const dp = Number.parseFloat(String(depositAmount).trim());
                const pfSafe = Number.isFinite(pf) && pf >= 0 ? pf : 0;
                const dpSafe = Number.isFinite(dp) && dp >= 0 ? dp : 0;

                setOnboardingProcessingFees(String(pfSafe));
                setOnboardingDepositAmount(String(dpSafe));
                setOnboardingFeeRemarks(remarksValue);

                setCostBreakdown((prev) =>
                  prev.map((row) => {
                    if (row.label === "Processing fees") {
                      return {
                        ...row,
                        value: `₹${pfSafe.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                      };
                    }
                    if (row.label === "Deposit amount") {
                      return {
                        ...row,
                        value: `₹${dpSafe.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                      };
                    }
                    return row;
                  }),
                );

                setCostBreakdownRaw((prev) => ({
                  ...prev,
                  processing_fees: pfSafe,
                  refundable_amount: dpSafe,
                }));

                const planPrice = Number.isFinite(
                  Number(costBreakdownRaw.planprice),
                )
                  ? Number(costBreakdownRaw.planprice)
                  : 0;
                const coreTotal = planPrice + pfSafe + dpSafe;
                setTotalPayable(
                  `₹${coreTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                );
              }}
              cashHandlingFees={cashPaymentCharge}
              onCashHandlingFeesChange={(value) => {
                const n = Number.parseFloat(String(value).trim());
                const v = Number.isFinite(n) && n >= 0 ? n : 0;
                setCashPaymentCharge(String(v));
                setCostBreakdown((prev) =>
                  prev.map((row) =>
                    row.label === "Cash Handling Fees"
                      ? {
                          ...row,
                          value: `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                        }
                      : row,
                  ),
                );
              }}
              promoCodeOptions={promoCodeOptions}
              promoCodesLoading={promoCodesLoading}
              couponApplyLoading={couponApplyLoading}
              newPlanWindowDisplay={
                newPlanWindowForCreate
                  ? {
                      start: newPlanWindowForCreate.planStartDisplay,
                      end: newPlanWindowForCreate.planEndDisplay,
                    }
                  : null
              }
            />
          )}
        </Paper>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "flex-end",
            alignItems: "stretch",
            gap: 2,
            mt: { xs: 3, sm: 4 },
            width: "100%",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            fullWidth={false}
            sx={{
              borderColor: THEME_PRIMARY,
              color: THEME_PRIMARY,
              minHeight: 42,
              px: 4,
              py: 1,
              borderRadius: "4px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                borderColor: THEME_PRIMARY_LIGHT,
                bgcolor: THEME_PRIMARY_TINT,
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disabled={
              (activeStep === 0 &&
                (selectedHubId === null ||
                  customerKycState.loading ||
                  (customerKycState.checked &&
                    !customerKycState.hasKycRecord))) ||
              (activeStep === 1 &&
                (selectedModelId == null ||
                  selectedModelId === "" ||
                  kycModelSelectionInvalid)) ||
              (activeStep === 2 &&
                (selectedVehicleId == null || selectedVehicleId === "")) ||
              saveLoading ||
              vehicleUnderbookingLoading
            }
            onClick={
              activeStep === steps.length - 1
                ? handleSaveClick
                : () => void handleNext()
            }
            fullWidth={false}
            sx={{
              bgcolor: "#FFC107",
              color: "#000",
              minHeight: 42,
              px: 4,
              py: 1,
              borderRadius: "4px",
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "#FFB300" },
              "&.Mui-disabled": {
                bgcolor: "action.disabledBackground",
                color: "action.disabled",
              },
            }}
          >
            {saveLoading
              ? "Saving..."
              : vehicleUnderbookingLoading
                ? "Reserving vehicle..."
                : activeStep === steps.length - 1
                  ? "Save"
                  : "Next"}
          </Button>
        </Box>

        <Dialog
          open={saveConfirmOpen}
          onClose={() => {
            if (saveLoading) return;
            setSaveConfirmOpen(false);
          }}
          maxWidth="sm"
          fullWidth
          aria-labelledby="create-booking-save-confirm-title"
          aria-describedby="create-booking-save-confirm-desc"
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(22, 67, 135, 0.12)",
              boxShadow:
                "0 20px 50px rgba(22, 67, 135, 0.18), 0 4px 16px rgba(0, 0, 0, 0.06)",
            },
          }}
          BackdropProps={{
            sx: { backgroundColor: "rgba(22, 67, 135, 0.35)" },
          }}
        >
          <Box
            sx={{
              height: 4,
              width: "100%",
              background: `linear-gradient(90deg, ${THEME_PRIMARY} 0%, ${THEME_PRIMARY_LIGHT} 100%)`,
            }}
          />
          <Box sx={{ px: 3, pt: 3, pb: 0, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                bgcolor: THEME_PRIMARY_TINT,
                color: THEME_PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid rgba(22, 67, 135, 0.15)`,
              }}
            >
              <NoteAddRoundedIcon sx={{ fontSize: 32 }} aria-hidden />
            </Box>
            <DialogTitle
              id="create-booking-save-confirm-title"
              sx={{
                p: 0,
                mb: 1,
                fontSize: "1.25rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: THEME_PRIMARY,
                lineHeight: 1.3,
              }}
            >
              Create this booking?
            </DialogTitle>
          </Box>
          <DialogContent sx={{ px: 3, pt: 0, pb: 1, textAlign: "center" }}>
            <Typography
              id="create-booking-save-confirm-desc"
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.65,
                maxWidth: 320,
                mx: "auto",
              }}
            >
              The booking will be created for this customer. If payment is
              required, you&apos;ll complete it right after you confirm.
            </Typography>
            {paymentMethod === "online" ? (
              <FormControl sx={{ mt: 2, width: "100%", textAlign: "center" }}>
                <FormLabel
                  sx={{
                    color: THEME_PRIMARY,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    mb: 1,
                    "&.Mui-focused": { color: THEME_PRIMARY },
                  }}
                >
                  Payment request type
                </FormLabel>
                <RadioGroup
                  row
                  value={postSavePaymentMode}
                  onChange={(e) =>
                    setPostSavePaymentMode(
                      e.target.value as "payment_link" | "qr_code",
                    )
                  }
                  sx={{ justifyContent: "center", gap: 2 }}
                >
                  <FormControlLabel
                    value="payment_link"
                    control={<Radio size="small" />}
                    label="Payment link"
                  />
                  <FormControlLabel
                    value="qr_code"
                    control={<Radio size="small" />}
                    label="QR code"
                  />
                </RadioGroup>
              </FormControl>
            ) : null}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 1,
              gap: 1.25,
              flexDirection: { xs: "column-reverse", sm: "row" },
              justifyContent: "stretch",
              "& .MuiButton-root": {
                minHeight: 44,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
              },
            }}
          >
            <Button
              onClick={() => setSaveConfirmOpen(false)}
              disabled={saveLoading}
              variant="outlined"
              fullWidth
              sx={{
                borderColor: THEME_PRIMARY,
                color: THEME_PRIMARY,
                "&:hover": {
                  borderColor: THEME_PRIMARY_LIGHT,
                  bgcolor: THEME_PRIMARY_TINT,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={saveLoading}
              variant="contained"
              fullWidth
              disableElevation
              sx={{
                bgcolor: THEME_PRIMARY,
                color: "#fff",
                boxShadow: "0 4px 14px rgba(22, 67, 135, 0.35)",
                "&:hover": {
                  bgcolor: THEME_PRIMARY_LIGHT,
                  boxShadow: "0 6px 18px rgba(22, 67, 135, 0.4)",
                },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "action.disabled",
                },
              }}
            >
              Create booking
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={onlineCollectionDialogOpen}
          onClose={() => {
            if (onlineCollectionLoading) return;
            verifyPaymentPollAbortRef.current = true;
            setVerifyPaymentLoading(false);
            const done = onlineCollectionBookingDone;
            setOnlineCollectionDialogOpen(false);
            setOnlineCollectionError(null);
            setOnlineCollectionPaymentLink("");
            setOnlineCollectionQrSrc("");
            setOnlineCollectionOrderId("");
            setOnlineCollectionVerifyEntityId(null);
            setOnlineCollectionBookingDone(false);
            if (done) navigateToBookingManagementAfterSuccess();
          }}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={onlineCollectionLoading}
          aria-labelledby="create-booking-online-collection-title"
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid rgba(22, 67, 135, 0.12)",
              boxShadow:
                "0 20px 50px rgba(22, 67, 135, 0.18), 0 4px 16px rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <DialogTitle
            id="create-booking-online-collection-title"
            sx={{
              fontSize: "1.15rem",
              fontWeight: 700,
              color: THEME_PRIMARY,
              pb: 1,
            }}
          >
            {postSavePaymentMode === "payment_link"
              ? "Payment link"
              : "Payment QR code"}
          </DialogTitle>
          <DialogContent sx={{ textAlign: "center", pt: 0 }}>
            {onlineCollectionLoading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress sx={{ color: THEME_PRIMARY }} />
              </Box>
            ) : null}
            {onlineCollectionError ? (
              <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                {onlineCollectionError}
              </Alert>
            ) : null}
            {!onlineCollectionLoading &&
            postSavePaymentMode === "payment_link" &&
            onlineCollectionPaymentLink ? (
              <Box sx={{ mt: 1, textAlign: "left", width: "100%" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  <Link
                    href={onlineCollectionPaymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      flex: 1,
                      wordBreak: "break-all",
                      fontSize: "0.95rem",
                      textAlign: "left",
                    }}
                  >
                    {onlineCollectionPaymentLink}
                  </Link>
                  <IconButton
                    aria-label="Copy payment link"
                    size="small"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(onlineCollectionPaymentLink)
                        .then(() =>
                          showNotification("Payment link copied.", "success"),
                        )
                        .catch(() =>
                          showNotification("Could not copy link.", "error"),
                        );
                    }}
                    sx={{ flexShrink: 0, mt: -0.25 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ) : null}
            {!onlineCollectionLoading &&
            postSavePaymentMode === "qr_code" &&
            onlineCollectionQrSrc ? (
              <Box sx={{ mt: 1, width: "100%", textAlign: "center" }}>
                <Box
                  component="img"
                  src={onlineCollectionQrSrc}
                  alt="Payment QR code"
                  sx={{
                    maxWidth: 280,
                    width: "100%",
                    height: "auto",
                    mx: "auto",
                    display: "block",
                    borderRadius: 1,
                    border: "1px solid rgba(22, 67, 135, 0.12)",
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  sx={{ width: "100%", mt: 1.5 }}
                >
                  <Link
                    href={onlineCollectionQrSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      flex: 1,
                      wordBreak: "break-all",
                      fontSize: "0.9rem",
                      textAlign: "left",
                    }}
                  >
                    {onlineCollectionQrSrc}
                  </Link>
                  <IconButton
                    aria-label="Copy QR link"
                    size="small"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(onlineCollectionQrSrc)
                        .then(() =>
                          showNotification("QR link copied.", "success"),
                        )
                        .catch(() =>
                          showNotification("Could not copy link.", "error"),
                        );
                    }}
                    sx={{ flexShrink: 0, mt: -0.25 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 2,
              pt: 1,
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {postSavePaymentMode === "qr_code" &&
            onlineCollectionQrSrc &&
            !onlineCollectionLoading ? (
              <Button
                variant="contained"
                disabled={verifyPaymentLoading}
                onClick={() => void handleVerifyQrPayment()}
                sx={{
                  bgcolor: THEME_PRIMARY,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 120,
                  "&:hover": { bgcolor: THEME_PRIMARY_LIGHT },
                }}
              >
                {verifyPaymentLoading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Verify"
                )}
              </Button>
            ) : null}
            <Button
              variant="outlined"
              disabled={onlineCollectionLoading}
              onClick={() => {
                if (onlineCollectionLoading) return;
                verifyPaymentPollAbortRef.current = true;
                setVerifyPaymentLoading(false);
                const done = onlineCollectionBookingDone;
                setOnlineCollectionDialogOpen(false);
                setOnlineCollectionError(null);
                setOnlineCollectionPaymentLink("");
                setOnlineCollectionQrSrc("");
                setOnlineCollectionOrderId("");
                setOnlineCollectionVerifyEntityId(null);
                setOnlineCollectionBookingDone(false);
                if (done) navigateToBookingManagementAfterSuccess();
              }}
              sx={{
                borderColor: THEME_PRIMARY,
                color: THEME_PRIMARY,
                textTransform: "none",
                fontWeight: 600,
                minWidth: 120,
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CreateBookingWizard;
